import React, { useEffect, useRef, useState } from "react";
import EditIcon from "@material-ui/icons/Edit";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteForeverIcon from "@material-ui/icons/DeleteForeverOutlined";
import CloseIcon from "@material-ui/icons/Close";
import {
  makeStyles,
  Grid,
  Box,
  Dialog,
  Drawer,
  Button,
  DialogContent,
  Menu,
  MenuItem,
} from "@material-ui/core";
import { Input as AntInput, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";

import {
  convertValuesFM,
  validateTwoDecimals,
  getResponse,
  converDateFormatForPicker,
  formatDateBySetting,
  downloadFile,
} from "../../../shared-resources/utils";
import { getPayloadTzAbbr } from "../tzUtils";
import CustomDatePicker from "../../../common-components/CustomDatePicker";
import CustomTextField from "../../../common-components/CustomTextField";
import ConfirmationDialog from "../../../common-components/ConfirmationDialog";
import {
  getIndirectDropdownsAPI,
  getIndirectHeaderAPI,
  getIndirectLaborAPI,
  getIndirectLaborODCAPI,
  getIndirectNonLaborODCAPI,
  updateIndirectLaborAPI,
  updateIndirectLaborODCAPI,
  postProjectDropdownAPI,
  getAccountDropdownAPI,
  getEmployeeDropdownAPI,
  createNewLaborAPI,
  createNewNonLaborAPI,
  updateLaborNotesAPI,
  updateNonLaborNotesAPI,
  deleteLaborNoteAPI,
  deleteNonLaborNoteAPI,
  deleteIndirectLaborAPI,
  deleteIndirectNonLaborAPI,
  downloadIndirectLaborAPI,
  uploadIndirectPlanFileAPI,
  uploadIndirectNonLaborDataAPI,
  downloadIndirectNonLaborTemplateAPI,
  getIndirectPlanFilesAPI,
  downloadIndirectPlanFileAPI,
  deleteIndirectPlanFileAPI,
} from "../../../api/financial-management-api";

const { TextArea } = AntInput;
import Loading from "../../../common-components/Loading";
import ActionMessages from "../../../common-components/ActionMessages";
import FileUpload from "../../../common-components/FileUpload";
import RenderLabor from "./RenderLabor";
import RenderLaborODC from "./RenderLaborODC";
import RenderIndirectExpenses from "./RenderIndirectExpenses";
import { monthsArray } from "../../../shared-resources/constants";
import {
  DownloadIcon,
  UploadIcon,
  DownloadTemplateIcon,
} from "../../../shared-resources/SVGIcons";

// Fixed-table column visibility (Labor/Non-Labor "Columns" pickers) persisted in localStorage
// so it survives navigating away and back, or a page refresh, for the rest of the browser
// session (and beyond, until the user clears it or hides a differently-shaped column set) —
// not just held in React state, which would reset the moment this component unmounts.
const LABOR_HIDDEN_COLS_KEY = "indirectLaborHiddenColumns";
const LABOR_ODC_HIDDEN_COLS_KEY = "indirectLaborODCHiddenColumns";

function loadHiddenColumns(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHiddenColumns(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort only — a full/blocked localStorage shouldn't break column toggling itself,
    // it just won't persist across a refresh.
  }
}

const defaultLabor = {
  projectId: "",
  emplId: "",
  startDate: "",
  activeSalary: 0,
  utilPct: 0,
  // Only meaningful when creating a row against the budget version (meritIncPct) or a new TBD
  // row on it (jobTitle) — ignored by the backend otherwise, per
  // INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §3. Left blank/omitted for every other case.
  meritIncPct: null,
  jobTitle: "",
  notes: "",
  financialYear: "",
};

const defaultNonLabor = {
  projectId: "",
  accountId: "",
  totalAmount: 0,
  notes: "",
  financialYear: "",
};

const compareKeys = (a, b) => {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

// Last name of an employeeName: "Last, First" → before the comma; "First Last" → final word.
const getLastName = (name) => {
  const n = String(name || "").trim();
  if (!n) return "";
  if (n.includes(",")) return n.split(",")[0].trim();
  const parts = n.split(/\s+/);
  return parts[parts.length - 1];
};

const compareText = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

// TBD rows (no employeeId) sort by their own id; real employee rows sort by last name, then full
// name, then employeeId (stable tiebreak for people sharing a name). The two groups otherwise
// keep their original relative order (a stable sort, returning 0 across groups) since no order
// between the groups themselves was specified.
const sortLaborRows = (data) => {
  if (!Array.isArray(data)) return data;
  return [...data].sort((a, b) => {
    const aIsTbd = !a.employeeId;
    const bIsTbd = !b.employeeId;
    if (aIsTbd !== bIsTbd) return 0;
    if (aIsTbd) return compareKeys(a.id, b.id);
    return (
      compareText(getLastName(a.employeeName), getLastName(b.employeeName)) ||
      compareText(a.employeeName, b.employeeName) ||
      compareKeys(a.employeeId, b.employeeId)
    );
  });
};

// Non-Labor rows sort by projectId.
const sortNonLaborRows = (data) => {
  if (!Array.isArray(data)) return data;
  return [...data].sort((a, b) => compareKeys(a.projectId, b.projectId));
};

const useStyles = makeStyles((theme) => ({
  selctLabel: {
    textAlign: "center",
    alignContent: "center",
  },
  // Fixed (left) pane of the Labor/Non-Labor tables keeps scrolling both ways — vertically in
  // sync with the scrollable pane, horizontally on its own since its identity columns are
  // wider than the pane itself. Only the VERTICAL scrollbar is hidden here (so just one
  // vertical scrollbar is ever visible, matching Fringe.js's leftBodyScroll/rightBodyScroll
  // pairing) — the horizontal one is left alone, or this column's own extra columns (Details,
  // Start Date, Job Title, ...) would be unreachable. `scrollbarWidth`/`-ms-overflow-style`
  // have no per-axis equivalent, so this only targets Chrome/Edge/Safari; Firefox falls back to
  // showing both scrollbars on this pane, which is a normal fallback, not a broken state.
  hiddenScrollbar: {
    "&::-webkit-scrollbar:vertical": {
      display: "none",
    },
  },
  tableTitle: {
    width: "220px",
    height: "36px",
    padding: "0 0 0 12px",
    fontSize: "15px",
    color: "#ffffff",
    backgroundColor: "#000",
    borderRadius: "0 10px 0 0",
    display: "flex",
    alignItems: "center",
  },
  utilizationLabel: {
    width: "130px",
    textAlign: "center",
    alignContent: "center",
  },
  indirectLabel: {
    width: "150px",
  },
  editIcon: {
    color: "#0070D2",
    fontSize: "14px",
    float: "left",
    marginTop: "2px",
  },
  editText: {
    color: "#0070D2",
    marginLeft: "4px",
    fontWeight: 600,
    fontSize: "13px",
  },
  editBtn: {
    border: "1px solid #0070D2",
    borderRadius: "20px",
    padding: "2px 8px",
    cursor: "pointer",
  },
  cancelBtn: {
    border: "1px solid #0070D2",
    borderRadius: "20px",
    padding: "4px 20px",
    cursor: "pointer",
  },
  saveBtn: {
    border: "1px solid #0070D2",
    borderRadius: "20px",
    padding: "4px 20px",
    cursor: "pointer",
    marginRight: "8px",
  },
  popupBtn: {
    fontSize: "12px",
    padding: "4px 8px !important",
    textTransform: "none",
    background: "#0176D3",
    marginRight: "8px",
  },

  newClass: {
    backgroundColor: "#f0f0f0", // Example of added extra data
    padding: "10px",
    borderRadius: "5px",
  },
  inputStyle: {
    width: "85px",
    border: "1px solid #ccc",
    borderRadius: "3px",
    padding: "2px",
  },
  centerAlignedData: {
    textAlign: "right",
  },
  addColumnTitle: {
    height: "28px",
    textAlign: "center",
    marginBottom: "11px",
    fontWeight: 600,
    paddingTop: "5px",
    color: "#ffff",
  },
  addDialog: {
    width: "700px",
    height: "auto",
    position: "relative",
  },
  popupHeader: {
    marginTop: "-20px",
    marginLeft: "-24px",
    marginRight: "-24px",
    background: "#4b5558",
  },
  closeIconAdd: {
    display: "inline-block",
    color: "black",
    background: "#ffff",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    cursor: "pointer",
    textAlign: "center",
    position: "absolute",
    top: "5px",
    right: "8px",
    fontSize: "13px",
    fontWeight: 600,
  },
  popupBtnsAdd: {
    textAlign: "center",
    marginTop: "24px",
  },
  cancelBtnPopup: {
    fontSize: "12px",
    padding: "3px 8px !important",
    textTransform: "none",
    border: "0.5px solid #0176D3",
    color: "#0176D3",
  },
  fieldTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
    fontSize: "13px",
  },
  // "Bulk Upload" drawer for Plan Files — same chrome as Weekly EAC's own upload drawer
  // (WeeklyEACTemplateActions.js), kept visually consistent across both upload flows.
  uploadDrawerSection: {
    width: "420px",
    position: "relative",
    padding: theme.spacing(0.75, 4),
  },
  uploadDrawerClose: {
    position: "absolute",
    cursor: "pointer",
    right: theme.spacing(0.25),
    top: theme.spacing(0.25),
  },
  uploadDrawerCloseIcon: {
    color: "#C4C4C4",
  },
  uploadDrawerTitle: {
    color: "#0082CA",
    fontSize: theme.spacing(2.25),
    marginBottom: theme.spacing(2),
  },
  uploadDrawerDescription: {
    marginBottom: theme.spacing(2),
  },
  uploadDrawerError: {
    color: "#c0392b",
    marginBottom: theme.spacing(2),
  },
}));

const IndirectSummaryTab = ({ isEdit, setIsEdit }) => {
  const classes = useStyles();

  const [downloading, setDownloading] = useState(false);

  // Excel plan-file attachments (upload/list/download) against the currently selected budget
  // version — Phase 1, stored as-is, no parsing. See
  // APIIntegrationFiles/INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §4. Upload lives in its own
  // "Bulk Upload" drawer (matching Weekly EAC's WeeklyEACTemplateActions.js pattern);
  // Attachments lives in its own list-only dialog — two separate icons, two separate surfaces.
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  // "attachment" stores the file as-is (uploadIndirectPlanFileAPI); "data" additionally parses
  // its "Indirect Non-Labor" sheet and applies it to the non-labor forecast
  // (uploadIndirectNonLaborDataAPI). See
  // APIIntegrationFiles/INDIRECT_NONLABOR_UPLOAD_FRONTEND_GUIDE.md.
  const [uploadMode, setUploadMode] = useState("attachment");
  const [showPlanFilesDialog, setShowPlanFilesDialog] = useState(false);
  const [planFiles, setPlanFiles] = useState([]);
  const [planFilesLoading, setPlanFilesLoading] = useState(false);
  const [planFileUploading, setPlanFileUploading] = useState(false);
  const [planFileError, setPlanFileError] = useState("");

  const [laborData, setLaborDataRaw] = useState([]);
  // Transparent wrapper — every existing setLaborData(...) call site (plain array or functional
  // updater) keeps working unchanged, but the result is always kept sorted per sortLaborRows
  // (TBD rows by id, real employee rows by last name) so originalIndex (assigned by
  // PaginationData off of this same array) stays correct everywhere it's used.
  const setLaborData = (valueOrUpdater) => {
    setLaborDataRaw((prev) =>
      sortLaborRows(
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(prev)
          : valueOrUpdater,
      ),
    );
  };
  const [filteredLabor, setFilteredLabor] = useState([]);
  const [laborAdjustments, setLaborAdjustments] = useState([]);
  // New top-level `totals` key on every GET /get-indirect-labor response (every version, not
  // just the budget one) — dollar totals + distinct-employee headcount per month, rendered at
  // the top of the Labor grid. See INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §2.
  const [laborTotals, setLaborTotals] = useState(null);
  const [editedLaborData, setEditedLaborData] = useState([]);
  // Fixed-table column visibility for Labor/Non-Labor — same "Columns" picker UX as the
  // Waterfall/EAC report's ThreeYearView.js. Every column is visible by default (empty array
  // = nothing hidden) unless localStorage already has a saved selection from a previous visit;
  // each holds the array of currently-HIDDEN column keys, not the visible ones.
  const [hiddenLaborCols, setHiddenLaborCols] = useState(() =>
    loadHiddenColumns(LABOR_HIDDEN_COLS_KEY),
  );
  const [hiddenLaborODCCols, setHiddenLaborODCCols] = useState(() =>
    loadHiddenColumns(LABOR_ODC_HIDDEN_COLS_KEY),
  );

  const [originalLaborData, setOriginalLaborData] = useState([]);
  const [laborODCData, setLaborODCDataRaw] = useState([]);
  // Same transparent-wrapper approach as setLaborData above, keeping the Non-Labor table sorted
  // by projectId (sortNonLaborRows) so originalIndex stays correct everywhere it's used.
  const setLaborODCData = (valueOrUpdater) => {
    setLaborODCDataRaw((prev) =>
      sortNonLaborRows(
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(prev)
          : valueOrUpdater,
      ),
    );
  };
  const [filteredlaborODCData, setFilteredLaborODCData] = useState([]);
  const [editedLaborODCData, setEditedLaborODCData] = useState([]);
  const [originalLaborODCData, setOriginalLaborODCData] = useState([]);
  const [IndirectExpensesData, setIndirectExpensesData] = useState([]);
  const [filterIndirectExpensesData, setFilterIndirectExpensesData] = useState(
    [],
  );
  const [editedIndirectExpensesData, setEditedIndirectExpensesData] = useState(
    [],
  );
  const [originalIndirectExpensesData, setOriginalIndirectExpensesData] =
    useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [msgType, setMsgType] = useState();
  const [msgText, setMsgText] = useState();
  const [emptyErrMsg, setEmptyErrMsg] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [subDepartmentList, setSubDepartmentList] = useState([]);
  const [selectedSubDepartment, setSelectedSubDepartment] = useState("");
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [versionList, setVersionList] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [versionStatus, setVersionStatus] = useState("");

  const [isEditDetails, setIsEditDetails] = useState(false);
  const [laborNotesHistory, setLaborNotesHistory] = useState([]);
  const [nonLaborNotesHistory, setNonLaborNotesHistory] = useState([]);
  const [selectedLabor, setSelectedLabor] = useState();
  const [selectedNonLabor, setSelectedNonLabor] = useState();
  const [showDialogLabor, setShowDialogLabor] = useState(false);
  const [showDialogNew, setShowDialogNew] = useState(false);
  const [laborFormData, setLaborFormData] = useState(defaultLabor);
  const [nonLaborFormData, setNonLaborFormData] = useState(defaultNonLabor);
  const PlanningIndirectNonLabourRef = useRef(null);
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");

  const [projectOptions, setProjectOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState("");
  const [deleteType, setDeleteType] = useState(null);
  const [laborType, setLaborType] = useState("");

  const LaborRef = useRef(null);
  const LaborODCRef = useRef(null);
  const IndirectExpensesRef = useRef(null);
  const generalSettingsData = useSelector(
    (state) => state.adminReducer.generalSettingsData,
  );

  useEffect(() => {
    if (!selectedDepartment || !selectedSubDepartment || !laborType) return;

    (async () => {
      const insertData = {
        department: selectedDepartment,
        subDepartment: selectedSubDepartment,
      };
      const [projectRes, accountRes, employeeRes] = await Promise.all([
        postProjectDropdownAPI(insertData),
        getAccountDropdownAPI(laborType),
        getEmployeeDropdownAPI(),
      ]);

      const mapProjectOptions = (data) =>
        data.map((item) => ({
          value: item.clin,
          label: item.display_text,
        }));

      const mapAccountOptions = (data) =>
        data.map((item) => ({
          value: item.acct_id,
          label: item.display_text,
        }));

      const mapEmployeeOptions = (data) =>
        data.map((item) => ({
          value: item.empl_id,
          label: item.display_text,
          activeAnnualSalary: item.active_annual_salary,
        }));

      if (projectRes?.status === 200) {
        setProjectOptions(mapProjectOptions(projectRes.data || []));
      } else {
        setProjectOptions([]);
      }

      if (accountRes?.status === 200) {
        setAccountOptions(mapAccountOptions(accountRes.data || []));
      } else {
        setAccountOptions([]);
      }

      if (employeeRes?.status === 200) {
        setEmployeeOptions(mapEmployeeOptions(employeeRes.data || []));
      } else {
        setEmployeeOptions([]);
      }
    })();
  }, [selectedDepartment, selectedSubDepartment, laborType]);

  useEffect(() => {
    getIndirectDropdownsAPI().then((response) => {
      if (response.status === 200) {
        const departmentData = response.data.map((department) => ({
          ...department,
          value: department.department,
          label: department.department,
        }));

        setDepartmentList(departmentData);
        setLoading(false);
      } else if (response.status === 400) {
        setDepartmentList([]);
        setYears([]);
        setLoading(false);
      }
      if (response.status === 400) {
        setLoading(false);
        setYears([]);

        setDepartmentList([]);
      }
    });
  }, []);

  // These three cascading effects all depend on `departmentList` in addition to their own
  // selection level, because `refreshDropdownsPreserveSelection` (called after Add New) hands
  // in a freshly-fetched departmentList so newly-added rows/options show up. But a refresh like
  // that is NOT a user picking a new department/sub-department/year — every effect below used
  // to unconditionally reset (and re-default to the first option of) everything downstream of
  // it whenever ANY of its deps changed, including a mere departmentList reference change with
  // the exact same selection still valid underneath. That's what caused "Add New" to silently
  // reset to the first year/version: the refresh alone was enough to re-trigger the default-
  // picking logic. Each effect now only resets/re-defaults a level when the CURRENT selection
  // at that level genuinely no longer exists in the fresh data (a real change, or the selected
  // item was removed) — otherwise it keeps the selection exactly as the user left it.
  useEffect(() => {
    if (!selectedDepartment) {
      setSelectedSubDepartment("");
      setSelectedYear("");
      setSelectedVersion("");
      setSubDepartmentList([]);
      return;
    }

    const selectedDept = departmentList.find(
      (dept) => dept.department === selectedDepartment,
    );

    if (!selectedDept || selectedDept.subDeptDropdown.length === 0) {
      setSelectedSubDepartment("");
      setSelectedYear("");
      setSelectedVersion("");
      setSubDepartmentList([]);
      return;
    }

    const subDeptList = selectedDept.subDeptDropdown.map((sub) => ({
      ...sub,
      value: sub.subDepartment,
      label: sub.subDepartment,
    }));

    setSubDepartmentList(subDeptList);

    const stillValid = subDeptList.some(
      (sub) => sub.subDepartment === selectedSubDepartment,
    );
    if (!stillValid) {
      setSelectedSubDepartment("");
      setSelectedYear("");
      setSelectedVersion("");
    }
  }, [selectedDepartment, departmentList]);

  useEffect(() => {
    if (!selectedSubDepartment || !selectedDepartment) {
      setSelectedYear("");
      setSelectedVersion("");
      setVersionList([]);
      return;
    }

    const selectedDept = departmentList.find(
      (dept) => dept.department === selectedDepartment,
    );

    const selectedSub = selectedDept?.subDeptDropdown.find(
      (sub) => sub.subDepartment === selectedSubDepartment,
    );

    if (!selectedSub || selectedSub.fyDropdown.length === 0) {
      setSelectedYear("");
      setSelectedVersion("");
      setVersionList([]);
      return;
    }

    const yearOptions = selectedSub.fyDropdown.map((fy) => ({
      value: fy.financialYear,
      label: fy.financialYear,
    }));
    setYears(yearOptions);

    const targetFy =
      selectedSub.fyDropdown.find((fy) => fy.financialYear === selectedYear) ||
      selectedSub.fyDropdown[0];
    setSelectedYear(targetFy.financialYear);

    const versionOptions = targetFy.version.map((v) => ({
      value: v.version,
      label: v.versionName,
      status: v.status,
      isBudget: v.isBudget,
      isEditableBudget: v.isEditableBudget,
    }));
    setVersionList(versionOptions);

    const targetVersion =
      targetFy.version.find((v) => v.version === selectedVersion) ||
      targetFy.version[0];
    setSelectedVersion(targetVersion.version);
    setVersionStatus(targetVersion.status);
  }, [selectedSubDepartment, selectedDepartment, departmentList]);
  useEffect(() => {
    if (!selectedYear || !selectedSubDepartment || !selectedDepartment) return;

    const selectedDept = departmentList.find(
      (dept) => dept.department === selectedDepartment,
    );

    const selectedSub = selectedDept?.subDeptDropdown.find(
      (sub) => sub.subDepartment === selectedSubDepartment,
    );

    const selectedFy = selectedSub?.fyDropdown.find(
      (fy) => fy.financialYear === selectedYear,
    );

    if (!selectedFy || selectedFy.version.length === 0) {
      setVersionList([]);
      setSelectedVersion("");
      setVersionStatus("");
      return;
    }

    const versionOptions = selectedFy.version.map((v) => ({
      value: v.version,
      label: v.versionName,
      status: v.status,
      isBudget: v.isBudget,
      isEditableBudget: v.isEditableBudget,
    }));

    setVersionList(versionOptions);

    const targetVersion =
      selectedFy.version.find((v) => v.version === selectedVersion) ||
      selectedFy.version[0];
    setSelectedVersion(targetVersion.version);
    setVersionStatus(targetVersion.status);
  }, [selectedYear, selectedSubDepartment, selectedDepartment, departmentList]);
  useEffect(() => {
    if (
      selectedDepartment === "" ||
      selectedSubDepartment === "" ||
      selectedYear === "" ||
      selectedVersion === ""
    )
      return;

    // Guard against firing with a stale version still left over from before
    // a year change. The effect above this one re-resolves selectedVersion
    // for the newly-picked year, but when the same version value also
    // exists under the new year (e.g. every year has a "liveEac" row), that
    // setSelectedVersion call is a no-op — same string in, same string out —
    // so React never re-renders for it, and selectedYear would otherwise be
    // the ONLY thing that actually changed. Depending on selectedVersion
    // alone (as before) then means this effect never re-fires for that case,
    // and the page silently keeps showing the old year's data. Including
    // selectedYear below fixes that, but on its own re-introduces a
    // different bug: when the version DOES change, this effect fires
    // immediately on the year-change render too, one render before
    // selectedVersion has caught up — fetching with the new year and the
    // old, now-invalid version. This check skips exactly that one stale
    // render; the effect fires again (deps readjust) as soon as
    // selectedVersion is valid for selectedYear, so every real change
    // (whether or not the version string happens to repeat) ends up with
    // exactly one fetch.
    const selectedDeptForGuard = departmentList.find(
      (dept) => dept.department === selectedDepartment,
    );
    const selectedSubForGuard = selectedDeptForGuard?.subDeptDropdown.find(
      (sub) => sub.subDepartment === selectedSubDepartment,
    );
    const selectedFyForGuard = selectedSubForGuard?.fyDropdown.find(
      (fy) => fy.financialYear === selectedYear,
    );
    const versionIsValidForYear = selectedFyForGuard?.version.some(
      (v) => v.version === selectedVersion,
    );
    if (!versionIsValidForYear) return;

    setLoading(true);

    getIndirectHeaderAPI(selectedYear, selectedVersion).then((response) => {
      if (response.status === 200) {
        setMonths(response.data);

        getIndirectLaborAPI({
          department: selectedDepartment,
          subDepartment: selectedSubDepartment,
          financialYear: selectedYear,
          version: selectedVersion,
        }).then((response) => {
          if (response.status === 200) {
            setLaborData(response.data.laborDetails);
            setLaborAdjustments(response.data.adjustments);
            setLaborTotals(response.data.totals || null);
          }
        });

        getIndirectLaborODCAPI({
          department: selectedDepartment,
          subDepartment: selectedSubDepartment,
          financialYear: selectedYear,
          version: selectedVersion,
        }).then((response) => {
          if (response.status === 200) {
            setLaborODCData(response.data);
            setEditedLaborData(response.data);
          }

          getIndirectNonLaborODCAPI({
            department: selectedDepartment,
            subDepartment: selectedSubDepartment,
            financialYear: selectedYear,
            version: selectedVersion,
          }).then((response) => {
            if (response.status === 200) {
              setIndirectExpensesData(response.data);
              setEditedIndirectExpensesData(response.data);
            }
            setLoading(false);
          });
        });
      }
    });
    // selectedYear IS a dependency here (see the versionIsValidForYear guard
    // above for why it has to be — the old assumption that selectedVersion
    // always changes whenever selectedYear does isn't true when the same
    // version value exists under both years) — the guard is what keeps this
    // to exactly one fetch per real change instead of reintroducing the
    // double-fetch-on-year-change bug the dependency list used to avoid.
  }, [
    selectedDepartment,
    selectedSubDepartment,
    selectedYear,
    selectedVersion,
  ]);
  useEffect(() => {
    if (getResponse(generalSettingsData, []).length > 0) {
      setDateFormat(
        converDateFormatForPicker(
          getResponse(generalSettingsData, [])[0].date_format,
        ),
      );
    }
  }, [generalSettingsData]);
  const onDeleteIconClick = (item) => {
    setDeleteItem(item.id);
    setDeleteType("labor");
    setConfirmOpen(true);
  };
  const onDeleteNonLaborClick = (item) => {
    setDeleteItem(item.projectAccountMappingId);
    setDeleteType("nonLabor");
    setConfirmOpen(true);
  };
  const onDeletePlanFileClick = (file) => {
    setDeleteItem(file.id);
    setDeleteType("planFile");
    setConfirmOpen(true);
  };

  const cancelConfirmClick = () => {
    setConfirmOpen(false);
    setDeleteItem(null);
    setDeleteType(null);
  };

  const okConfirmClick = async (val) => {
    if (!val) {
      return;
    }

    if (deleteType === "laborNote" || deleteType === "nonLaborNote") {
      const isNonLabor = deleteType === "nonLaborNote";
      const deleteApi = isNonLabor ? deleteNonLaborNoteAPI : deleteLaborNoteAPI;

      setLoading(true);
      const res = await deleteApi(deleteItem);
      setConfirmOpen(false);
      setDeleteItem(null);
      setDeleteType(null);
      setLoading(false);

      if (res?.status === 200) {
        setEmptyErrMsg(true);
        setMsgType("success");
        setMsgText("Note deleted successfully.");

        const updatedNotes = res.data?.notes ?? [];

        if (isNonLabor) {
          setNonLaborNotesHistory(toNotesHistory(updatedNotes));
          // Patch the underlying row too, not just this dialog's local history state —
          // otherwise closing and reopening "View" re-reads the table's stale
          // item.listOfNotes and the just-deleted note reappears.
          setIndirectExpensesData((prev) =>
            prev.map((row) =>
              row.projectAccountMappingId === deleteItem.id
                ? { ...row, listOfNotes: updatedNotes }
                : row,
            ),
          );
        } else {
          setLaborNotesHistory(toNotesHistory(updatedNotes));
          setLaborData((prev) =>
            prev.map((row) =>
              row.id === deleteItem.id
                ? { ...row, listOfNotes: updatedNotes }
                : row,
            ),
          );
        }
      } else {
        setEmptyErrMsg(true);
        setMsgType("error");
        setMsgText(res?.message || "Failed to delete note.");
      }
      return;
    }

    // Plan-file deletes are self-contained to the Attachments dialog — they use its own
    // loading/error state (planFilesLoading/planFileError, already shown inside that dialog)
    // rather than the page-wide loading overlay/ActionMessages toast the Labor/Non-Labor
    // deletes below use, and refresh via fetchPlanFiles() instead of re-fetching the whole
    // Labor/Non-Labor grid. See INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §4 (2026-09-22).
    if (deleteType === "planFile") {
      setPlanFilesLoading(true);
      const deleteResponse = await deleteIndirectPlanFileAPI(deleteItem);
      setConfirmOpen(false);
      setDeleteItem(null);
      setDeleteType(null);
      setPlanFilesLoading(false);
      if (deleteResponse?.status === 200) {
        setPlanFileError("");
        fetchPlanFiles();
      } else {
        setPlanFileError(
          deleteResponse?.message || "Unable to delete the file.",
        );
      }
      return;
    }

    setLoading(true);

    const isNonLabor = deleteType === "nonLabor";

    const deleteApi = isNonLabor
      ? deleteIndirectNonLaborAPI
      : deleteIndirectLaborAPI;

    const refreshApi = isNonLabor
      ? getIndirectLaborODCAPI
      : getIndirectLaborAPI;

    const deleteResponse = await deleteApi(deleteItem);

    if (deleteResponse.status === 200) {
      setConfirmOpen(false);
      setDeleteItem(null);
      setDeleteType(null);
      setEmptyErrMsg(true);
      setMsgType("success");
      setMsgText("Item deleted successfully.");

      const refreshResponse = await refreshApi({
        department: selectedDepartment,
        subDepartment: selectedSubDepartment,
        financialYear: selectedYear,
        version: selectedVersion,
      });

      if (refreshResponse.status === 200) {
        if (isNonLabor) {
          setLaborODCData(refreshResponse.data);
          setEditedLaborData(refreshResponse.data);
        } else {
          setLaborData(refreshResponse.data.laborDetails);
          setLaborAdjustments(refreshResponse.data.adjustments);
          setLaborTotals(refreshResponse.data.totals || null);
        }
      }
    } else {
      setConfirmOpen(false);
      setDeleteItem(null);
      setDeleteType(null);
      setEmptyErrMsg(true);
      setMsgType("error");
      setMsgText(deleteResponse.message);
    }

    setLoading(false);
  };

  const handleScrollTable = (sourceTable) => {
    if (sourceTable === "PlanningIndirectNonLabour") {
      if (LaborRef.current)
        LaborRef.current.scrollLeft =
          PlanningIndirectNonLabourRef.current.scrollLeft;
      if (LaborODCRef.current)
        LaborODCRef.current.scrollLeft =
          PlanningIndirectNonLabourRef.current.scrollLeft;
      if (IndirectExpensesRef.current)
        IndirectExpensesRef.current.scrollLeft =
          PlanningIndirectNonLabourRef.current.scrollLeft;
    } else if (sourceTable === "Labor") {
      if (PlanningIndirectNonLabourRef.current)
        PlanningIndirectNonLabourRef.current.scrollLeft =
          LaborRef.current.scrollLeft;
      if (LaborODCRef.current)
        LaborODCRef.current.scrollLeft = LaborRef.current.scrollLeft;
      if (IndirectExpensesRef.current)
        IndirectExpensesRef.current.scrollLeft = LaborRef.current.scrollLeft;
    } else if (sourceTable === "LaborODC") {
      if (PlanningIndirectNonLabourRef.current)
        PlanningIndirectNonLabourRef.current.scrollLeft =
          LaborODCRef.current.scrollLeft;
      if (LaborRef.current)
        LaborRef.current.scrollLeft = LaborODCRef.current.scrollLeft;
      if (IndirectExpensesRef.current)
        IndirectExpensesRef.current.scrollLeft = LaborODCRef.current.scrollLeft;
    } else if (sourceTable === "IndirectExpenses") {
      if (PlanningIndirectNonLabourRef.current)
        PlanningIndirectNonLabourRef.current.scrollLeft =
          IndirectExpensesRef.current.scrollLeft;
      if (LaborRef.current)
        LaborRef.current.scrollLeft = IndirectExpensesRef.current.scrollLeft;
      if (LaborODCRef.current)
        LaborODCRef.current.scrollLeft = IndirectExpensesRef.current.scrollLeft;
    }
  };
  const currentYear = new Date().getFullYear();

  const financialYearOptions = [
    {
      label: `${currentYear - 1}`,
      value: currentYear - 1,
    },
    {
      label: `${currentYear}`,
      value: currentYear,
    },
    {
      label: `${currentYear + 1}`,
      value: currentYear + 1,
    },
  ];
  const handleAddNewLabor = () => {
    setLaborType("LABOR");
    setShowDialogLabor(true);
    setLaborFormData(defaultLabor);
  };
  const handleAddNewNonLabor = () => {
    setLaborType("NON-LABOR");
    setShowDialogNew(true);
    setNonLaborFormData(defaultNonLabor);
  };
  // Keeps each note's index in the RAW backend listOfNotes array (what delete-*-note's
  // noteIndex refers to) attached as `idx`, surviving both the blank-entry filter and the
  // newest-first reverse used for display — so a displayed row's own idx can be sent straight
  // to the delete API instead of re-deriving it (which breaks the moment a blank entry anywhere
  // in the raw array shifts every later index).
  const toNotesHistory = (raw) => {
    const notes = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return notes
      .map((text, idx) => ({ text, idx }))
      .filter((entry) => entry.text && entry.text.trim() !== "")
      .reverse();
  };

  const onClickLaborDetails = (item) => {
    setIsEditDetails(true);
    setSelectedLabor({ id: item.id });
    setLaborNotesHistory(toNotesHistory(item.listOfNotes));
    setLaborFormData({ ...laborFormData, notes: "" });
    setShowDialogLabor(true);
  };
  const onClickNonLaborDetails = (item) => {
    setIsEditDetails(true);
    setSelectedNonLabor({
      id: item.projectAccountMappingId,
    });
    setNonLaborNotesHistory(toNotesHistory(item.listOfNotes));
    setNonLaborFormData({ ...nonLaborFormData, notes: "" });
    setShowDialogNew(true);
  };

  // Opens the same shared ConfirmationDialog used for deleting Labor/Non-Labor rows, instead of
  // deleting immediately — actual deletion happens in okConfirmClick's "laborNote" branch. `i` is
  // the clicked entry's position in the DISPLAYED list; its `idx` is the position in the
  // backend's raw listOfNotes array (per toNotesHistory above), which is what noteIndex means.
  const handleDeleteLaborNote = (i) => {
    setDeleteItem({ id: selectedLabor?.id, noteIndex: laborNotesHistory[i]?.idx });
    setDeleteType("laborNote");
    setConfirmOpen(true);
  };

  // Same confirm-dialog approach as handleDeleteLaborNote above.
  const handleDeleteNonLaborNote = (i) => {
    setDeleteItem({
      id: selectedNonLabor?.id,
      noteIndex: nonLaborNotesHistory[i]?.idx,
    });
    setDeleteType("nonLaborNote");
    setConfirmOpen(true);
  };

  const onAddLabor = () => {
    if (isEditDetails) {
      setLoading(true);
      const username =
        localStorage.getItem("Login_User_Name") ||
        localStorage.getItem("email") ||
        "";
      const now = new Date();
      const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzAbbr = getPayloadTzAbbr();
      const datePart = now
        .toLocaleDateString("en-US", {
          timeZone: systemTz,
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-");
      const timePart = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: systemTz,
      });
      const formattedTimestamp = `${datePart} ${timePart} ${tzAbbr}`;
      const insertData = {
        id: selectedLabor?.id,
        notes: laborFormData.notes
          ? `${username} - ${formattedTimestamp} - ${laborFormData.notes}`
          : `${username} - ${formattedTimestamp}`,
      };
      updateLaborNotesAPI(insertData).then((res) => {
        if (res.status === 200) {
          setShowDialogLabor(false);
          setLoading(false);
          setMsgType("success");
          setEmptyErrMsg(true);
          setIsEditDetails(false);
          setMsgText(res.data);
          getIndirectLaborAPI({
            department: selectedDepartment,
            subDepartment: selectedSubDepartment,
            financialYear: selectedYear,
            version: selectedVersion,
          }).then((response) => {
            if (response.status === 200) {
              setLaborData(response.data.laborDetails);
              setLaborAdjustments(response.data.adjustments);
              setLaborTotals(response.data.totals || null);
            }
          });
        }
        if (res.status === 400) {
          setLoading(false);
          setMsgType("error");
          setEmptyErrMsg(true);
          setMsgText(res.message);
        }
      });
    } else {
      const username =
        localStorage.getItem("Login_User_Name") ||
        localStorage.getItem("email") ||
        "";
      const now = new Date();
      const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzAbbr = getPayloadTzAbbr();
      const datePart = now
        .toLocaleDateString("en-US", {
          timeZone: systemTz,
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-");
      const timePart = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: systemTz,
      });
      const formattedTimestamp = `${datePart} ${timePart} ${tzAbbr}`;
      const insertData = {
        department: selectedDepartment,
        subDepartment: selectedSubDepartment,
        ...laborFormData,
        // jobTitle only applies to a new TBD row (no employee picked) on the budget version —
        // never send it otherwise, per INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §3.
        jobTitle:
          isBudgetVersion && !laborFormData.emplId
            ? laborFormData.jobTitle
            : undefined,
        notes: laborFormData.notes
          ? `${username} - ${formattedTimestamp} - ${laborFormData.notes}`
          : `${username} - ${formattedTimestamp}`,
      };

      setLoading(true);
      createNewLaborAPI(insertData, selectedVersion).then((res) => {
        if (res.status === 200) {
          setShowDialogLabor(false);
          setLoading(false);
          setMsgType("success");
          setEmptyErrMsg(true);
          setMsgText(res.data.message);

          refreshDropdownsPreserveSelection();

          getIndirectLaborAPI({
            department: selectedDepartment,
            subDepartment: selectedSubDepartment,
            financialYear: selectedYear,
            version: selectedVersion,
          }).then((response) => {
            if (response.status === 200) {
              setLaborData(response.data.laborDetails);
              setLaborAdjustments(response.data.adjustments);
              setLaborTotals(response.data.totals || null);
            }
          });
        }

        if (res.status === 400) {
          setLoading(false);
          setMsgType("error");
          setEmptyErrMsg(true);
          setMsgText(res.message);
        }
      });
    }
  };
  const onAddNonLabor = () => {
    if (isEditDetails) {
      setLoading(true);

      const username =
        localStorage.getItem("Login_User_Name") ||
        localStorage.getItem("email") ||
        "";
      const now = new Date();
      const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzAbbr = getPayloadTzAbbr();
      const datePart = now
        .toLocaleDateString("en-US", {
          timeZone: systemTz,
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-");
      const timePart = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: systemTz,
      });
      const formattedTimestamp = `${datePart} ${timePart} ${tzAbbr}`;
      const insertData = { ...selectedNonLabor };
      insertData.notes = nonLaborFormData.notes
        ? `${username} - ${formattedTimestamp} - ${nonLaborFormData.notes}`
        : `${username} - ${formattedTimestamp}`;

      updateNonLaborNotesAPI(insertData).then((res) => {
        if (res.status === 200) {
          setShowDialogNew(false);
          setLoading(false);
          setMsgType("success");
          setEmptyErrMsg(true);
          setIsEditDetails(false);
          setMsgText(res.data);

          getIndirectLaborODCAPI({
            department: selectedDepartment,
            subDepartment: selectedSubDepartment,
            financialYear: selectedYear,
            version: selectedVersion,
          }).then((response) => {
            if (response.status === 200) {
              setLaborODCData(response.data);
              setEditedLaborData(response.data);
            }
          });
        }

        if (res.status === 400) {
          setLoading(false);
          setMsgType("error");
          setEmptyErrMsg(true);
          setMsgText(res.message);
        }
      });
    } else {
      const username =
        localStorage.getItem("Login_User_Name") ||
        localStorage.getItem("email") ||
        "";
      const now = new Date();
      const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzAbbr = getPayloadTzAbbr();
      const datePart = now
        .toLocaleDateString("en-US", {
          timeZone: systemTz,
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-");
      const timePart = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: systemTz,
      });
      const formattedTimestamp = `${datePart} ${timePart} ${tzAbbr}`;
      const insertData = {
        department: selectedDepartment,
        subDepartment: selectedSubDepartment,
        ...nonLaborFormData,
        notes: nonLaborFormData.notes
          ? `${username} - ${formattedTimestamp} - ${nonLaborFormData.notes}`
          : `${username} - ${formattedTimestamp}`,
      };

      setLoading(true);
      createNewNonLaborAPI(insertData, selectedYear, selectedVersion).then(
        (res) => {
          if (res.status === 200) {
            setShowDialogNew(false);
            setLoading(false);
            setMsgType("success");
            setEmptyErrMsg(true);
            setMsgText(res.data.message);

            getIndirectLaborODCAPI({
              department: selectedDepartment,
              subDepartment: selectedSubDepartment,
              financialYear: selectedYear,
              version: selectedVersion,
            }).then((response) => {
              if (response.status === 200) {
                setLaborODCData(response.data);
                setEditedLaborData(response.data);
              }
            });
          }

          if (res.status === 400) {
            setLoading(false);
            setMsgType("error");
            setEmptyErrMsg(true);
            setMsgText(res.message);
          }
        },
      );
    }
  };

  const isClosed = (val) => {
    setEmptyErrMsg(!val);
  };
  const onChangeFormDataSelect = (val, key) => {
    const changedData = JSON.parse(JSON.stringify(laborFormData));
    changedData[key] = val;
    // Picking a real employee auto-fills Active Salary from
    // GET /financial/indirect/employee-dropdown's `active_annual_salary` — that field then goes
    // read-only (below) since it's Costpoint-sourced, same as it is everywhere else once a real
    // employee is attached to a row. Clearing the employee selection resets it back to 0 so a
    // stale value isn't left behind if the row goes back to being a TBD row.
    if (key === "emplId") {
      const selectedEmployee = employeeOptions.find((opt) => opt.value === val);
      changedData.activeSalary = selectedEmployee?.activeAnnualSalary ?? 0;
    }
    setLaborFormData(changedData);
  };

  const onChangeFormNonLaborSelect = (val, key) => {
    const changedData = JSON.parse(JSON.stringify(nonLaborFormData));
    changedData[key] = val;
    setNonLaborFormData(changedData);
  };
  const onValidateNumber = (e) => {
    const value = e.target.value.trim();

    // Allow empty, "-" or "." while typing
    if (value === "" || value === "-" || value === ".") {
      return { isValid: true, changeVal: value };
    }

    // Regex: digits with optional decimal (max 2 digits)
    const validNumber = /^-?\d+(\.\d{0,2})?$/;

    if (!validNumber.test(value)) {
      return { isValid: false, changeVal: 0 };
    }

    return {
      isValid: true,
      changeVal: value.endsWith(".") ? value : Number(value),
    };
  };

  const onChangeFormData = (e, key) => {
    const changedData = JSON.parse(JSON.stringify(laborFormData));

    if (key === "activeSalary" || key === "utilPct" || key === "meritIncPct") {
      const { isValid, changeVal } = onValidateNumber(e);
      if (!isValid) return false;
      changedData[key] = changeVal;
    } else {
      changedData[key] = e.target.value;
    }

    setLaborFormData(changedData);
  };
  const onChangeFormNonLaborData = (e, key) => {
    const changedData = JSON.parse(JSON.stringify(nonLaborFormData));

    if (key === "totalAmount") {
      const { isValid, changeVal } = onValidateNumber(e);
      if (!isValid) return false;
      changedData[key] = changeVal;
    } else {
      changedData[key] = e.target.value;
    }

    setNonLaborFormData(changedData);
  };
  const getDateMoment = (momentDate) => {
    if (!momentDate || !momentDate.isValid()) return null;

    return new Date(momentDate.year(), momentDate.month(), momentDate.date());
  };
  const handleEdit = () => {
    setIsEdit(true);
    // Project/Account/Employee dropdown options only ever get fetched once laborType is set
    // (previously only on "+ Add New" via handleAddNewLabor) — the inline Project ID dropdown
    // on existing TBD rows needs projectOptions populated as soon as edit mode starts too.
    setLaborType("LABOR");
    //Labor
    setOriginalLaborData([...laborData]);

    const initialLaborData = laborData.map((odc) => {
      const obj = {
        ...months.reduce((acc, month) => {
          const normalizedMonth = month.heading.split("-")[0].toLowerCase();
          acc[month.heading] =
            odc[normalizedMonth] !== undefined ? odc[normalizedMonth] : 0;
          return acc;
        }, {}),
        budgetSalary: odc.budgetSalary || 0,
        activeSalary: odc.activeSalary || 0,
        startDate: odc.startDate,
        projectId: odc.projectId,
        projectName: odc.projectName,
        utilPct: odc.utilPct || 0,
        // Missing entirely (not 0) whenever the field isn't applicable to the version being
        // viewed — carried through as-is (INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §2).
        meritIncPct: odc.meritIncPct,
        jobTitle: odc.jobTitle,
      };

      return obj;
    });

    setEditedLaborData(initialLaborData);

    //LaborOdc
    setOriginalLaborODCData([...laborODCData]); // <-- Save the original data

    const initialEditedLaborODCData = laborODCData.map((odc) => {
      const obj = {
        costPerEmployee: odc.costPerEmployee,
        numberOfEmployee: odc.numberOfEmployee,
        annualTarget: odc.annualTarget,

        // Process months
        ...months.reduce((acc, month) => {
          const normalizedMonth = month.heading.split("-")[0].toLowerCase();
          acc[month.heading] =
            odc[normalizedMonth] !== undefined ? odc[normalizedMonth] : 0;
          return acc;
        }, {}),
      };

      const total = months.reduce(
        (sum, month) => sum + (obj[month.heading] || 0),
        0,
      );
      obj.editingTotal = Number(total.toFixed(2));

      return obj;
    });

    setEditedLaborODCData(initialEditedLaborODCData);

    //IndirectExpenses
    setOriginalIndirectExpensesData([...IndirectExpensesData]); // Save original data

    const initialEditedIndirectExpensesData = IndirectExpensesData.map(
      (item) => {
        const obj = {
          costPerEmployee: item.costPerEmployee,
          numberOfEmployee: item.numberOfEmployee,
          annualTarget: item.annualTarget,

          ...months.reduce((acc, month) => {
            const normalizedMonth = month.heading.split("-")[0].toLowerCase();
            acc[month.heading] =
              item[normalizedMonth] !== undefined ? item[normalizedMonth] : 0;
            return acc;
          }, {}),
        };

        obj.editingTotal = months.reduce(
          (sum, month) => sum + (Number(obj[month.heading]) || 0),
          0,
        );

        return obj;
      },
    );

    setEditedIndirectExpensesData(initialEditedIndirectExpensesData);
  };

  // Handle saving the edited data
  const handleSave = () => {
    // TBD rows (no employeeId) have an editable Start Date — block the save instead of
    // silently persisting an empty value, since applyBudgetSalaryAndForecast needs a real
    // start date to know which forecast months to populate for that row.
    const hasMissingStartDate = editedLaborData.some(
      (odc) => !odc.employeeId && !odc.startDate,
    );
    if (hasMissingStartDate) {
      setMsgType("error");
      setEmptyErrMsg(true);
      setMsgText("Start Date is required for all labor rows.");
      return;
    }

    setLoading(true);

    const updatedLaborData = [...laborData];

    editedLaborData.forEach((odc, index) => {
      let updatedSalary = 0;

      months.forEach((month) => {
        const normalizedMonth = month.heading.split("-")[0].toLowerCase();
        const originalValue = odc[normalizedMonth];
        const editedValue = odc[month.heading];

        // A lone "-" means the user started typing a negative number but
        // never finished before saving — not a real value. Leave
        // updatedLaborData[index][normalizedMonth] at whatever laborData
        // already had (the pre-edit value, from the `[...laborData]` clone
        // above) instead of sending "-" to the API or silently saving it
        // as 0.
        if (
          editedValue !== undefined &&
          editedValue !== originalValue &&
          editedValue !== "-"
        ) {
          updatedLaborData[index][normalizedMonth] = editedValue;
        }

        const monthValue =
          parseFloat(updatedLaborData[index][normalizedMonth]) || 0;
        updatedSalary += monthValue;
      });
      updatedLaborData[index].budgetSalary = odc.budgetSalary;
      updatedLaborData[index].activeSalary = odc.activeSalary;
      updatedLaborData[index].startDate = odc.startDate;
      updatedLaborData[index].projectId = odc.projectId;
      updatedLaborData[index].projectName = odc.projectName;
      updatedLaborData[index].utilPct = odc.utilPct;
      // Both are picked up by the backend only on the budget version (meritIncPct) / for a TBD
      // row on it (jobTitle) and ignored otherwise — harmless to always send since isUpdated is
      // already set unconditionally on every row here, same as the fields above.
      updatedLaborData[index].meritIncPct = odc.meritIncPct;
      updatedLaborData[index].jobTitle = odc.jobTitle;
      updatedLaborData[index].isUpdated = true;
    });

    const updatedLaborODCData = [...laborODCData];

    editedLaborODCData.forEach((odc, index) => {
      let updatedAnnualTarget = 0;

      months.forEach((month) => {
        const normalizedMonth = month.heading.split("-")[0].toLowerCase();
        const originalValue = odc[normalizedMonth];
        const editedValue = odc[month.heading];

        // See the Labor block above: a lone "-" is an unfinished edit, not
        // a real value — keep the pre-edit value instead of saving "-".
        if (
          editedValue !== undefined &&
          editedValue !== originalValue &&
          editedValue !== "-"
        ) {
          updatedLaborODCData[index][normalizedMonth] = editedValue;
        }

        const monthValue =
          parseFloat(updatedLaborODCData[index][normalizedMonth]) || 0;
        updatedAnnualTarget += monthValue;
      });

      if (odc.costPerEmployee && odc.numberOfEmployee) {
        updatedAnnualTarget =
          parseFloat(odc.costPerEmployee) * parseFloat(odc.numberOfEmployee);
      } else if (odc.costPerEmployee && !odc.numberOfEmployee) {
        updatedAnnualTarget = parseFloat(odc.costPerEmployee);
      }

      updatedLaborODCData[index].annualTarget = updatedAnnualTarget;
      updatedLaborODCData[index].costPerEmployee = parseFloat(
        odc.costPerEmployee,
      );
      updatedLaborODCData[index].numberOfEmployee = parseFloat(
        odc.numberOfEmployee,
      );
      updatedLaborODCData[index].isUpdated = true;
    });

    const updatedIndirectExpensesData = [...IndirectExpensesData];

    editedIndirectExpensesData.forEach((odc, index) => {
      let updatedAnnualTarget = 0;

      months.forEach((month) => {
        const normalizedMonth = month.heading.split("-")[0].toLowerCase();
        const originalValue = odc[normalizedMonth];
        const editedValue = odc[month.heading];

        // See the Labor block above: a lone "-" is an unfinished edit, not
        // a real value — keep the pre-edit value instead of saving "-".
        if (
          editedValue !== undefined &&
          editedValue !== originalValue &&
          editedValue !== "-"
        ) {
          updatedIndirectExpensesData[index][normalizedMonth] = editedValue;
        }

        const monthValue =
          parseFloat(updatedIndirectExpensesData[index][normalizedMonth]) || 0;
        updatedAnnualTarget += monthValue;
      });

      if (odc.costPerEmployee && odc.numberOfEmployee) {
        updatedAnnualTarget =
          parseFloat(odc.costPerEmployee) * parseFloat(odc.numberOfEmployee);
      } else if (odc.costPerEmployee && !odc.numberOfEmployee) {
        updatedAnnualTarget = parseFloat(odc.costPerEmployee);
      }

      updatedIndirectExpensesData[index].annualTarget = updatedAnnualTarget;
      updatedIndirectExpensesData[index].costPerEmployee =
        parseFloat(odc.costPerEmployee) || 0;
      updatedIndirectExpensesData[index].numberOfEmployee = parseFloat(
        odc.numberOfEmployee,
      );
      updatedIndirectExpensesData[index].isUpdated = true;
    });

    const combinedData = [
      ...updatedLaborODCData,
      ...updatedIndirectExpensesData,
    ];

    (async () => {
      const [laborRes, odcRes] = await Promise.all([
        updateIndirectLaborAPI(
          {
            laborDetails: updatedLaborData,
            adjustments: laborAdjustments,
          },
          selectedVersion,
        ),
        updateIndirectLaborODCAPI(combinedData, selectedVersion),
      ]);

      if (laborRes?.status === 200 && odcRes?.status === 200) {
        setMsgType("success");
        setMsgText("Data saved successfully");

        // Exit edit mode BEFORE re-fetching below. Each setState after an
        // `await` is its own unbatched render in this React version, so if
        // setLaborData() below fired first, there would be one render where
        // isEdit is still true but laborData already reflects the fresh
        // (possibly different-length/order) server response while
        // editedLaborData is still sized for the pre-save data — crashing
        // calculateLaborMonthlyTotal's isEdit branch, which indexes
        // editedLaborData by laborData's position. Flipping isEdit first
        // routes that calculation to its non-edit branch instead, which
        // doesn't touch editedLaborData at all.
        setIsEdit(false);

        // Re-fetch from the server instead of trusting the locally-computed
        // values — the backend recalculates totals/adjustments on save, and
        // this keeps the grid in sync with it the same way the notes-save
        // flows above already do (updateLaborNotesAPI/updateNonLaborNotesAPI
        // both re-call their matching get API right after a successful save).
        const [laborGetRes, laborOdcGetRes, nonLaborGetRes] =
          await Promise.all([
            getIndirectLaborAPI({
              department: selectedDepartment,
              subDepartment: selectedSubDepartment,
              financialYear: selectedYear,
              version: selectedVersion,
            }),
            getIndirectLaborODCAPI({
              department: selectedDepartment,
              subDepartment: selectedSubDepartment,
              financialYear: selectedYear,
              version: selectedVersion,
            }),
            getIndirectNonLaborODCAPI({
              department: selectedDepartment,
              subDepartment: selectedSubDepartment,
              financialYear: selectedYear,
              version: selectedVersion,
            }),
          ]);

        if (laborGetRes?.status === 200) {
          setLaborData(laborGetRes.data.laborDetails);
          setLaborAdjustments(laborGetRes.data.adjustments);
          setLaborTotals(laborGetRes.data.totals || null);
        }
        if (laborOdcGetRes?.status === 200) {
          setLaborODCData(laborOdcGetRes.data);
          setEditedLaborData(laborOdcGetRes.data);
        }
        if (nonLaborGetRes?.status === 200) {
          setIndirectExpensesData(nonLaborGetRes.data);
          setEditedIndirectExpensesData(nonLaborGetRes.data);
        }
      } else {
        setMsgType("error");
        setMsgText("Error saving data");
      }

      setLoading(false);
      setEmptyErrMsg(true);
      setIsEdit(false);
    })();
  };

  // Handle canceling the edit
  const handleCancel = () => {
    //Labor
    setLaborData(originalLaborData);
    //LaborODC
    setLaborODCData(originalLaborODCData);
    //IndirectExpenses
    setIndirectExpensesData(originalIndirectExpensesData);

    setIsEdit(false);
  };

  // Handle INPUT changes in project salary
  const handleLaborSalaryChange = (index, value) => {
    const updatedLaborData = [...editedLaborData];

    // Update the 'salary' field for the employee at index
    const newSalary = parseFloat(value) || 0;
    updatedLaborData[index].cyPlusOne = Number(newSalary);
    updatedLaborData[index].total = Number(newSalary);

    // Update the monthly salary values based on the new salary (divide by 12)
    const monthlySalary = newSalary / 12;
    months.forEach((month) => {
      updatedLaborData[index][month] = monthlySalary; // Set all months to monthly salary
    });

    setEditedLaborData(updatedLaborData);
  };

  // Handle input changed in project per month salary

  const handleLaborMonthlySalaryChange = (rowIndex, monthHeading, value) => {
    if (value !== "" && value !== "-" && !/^-?\d*\.?\d{0,2}$/.test(value))
      return;
    const updatedLaborData = [...editedLaborData];
    const row = updatedLaborData[rowIndex];

    // Validate input to 2 decimals
    //const validatedValue = validateTwoDecimals(value);

    // Update the month value
    row[monthHeading] = value;

    // A lone "-" is just the user starting to type a negative number — keep
    // it in the cell (above), but don't recompute editingTotal/cyPlusOne off
    // of it yet, since it isn't a number. Once a digit follows (e.g. "-1"),
    // this falls through to the normal recalculation below, same as any
    // positive value.
    if (value === "-") {
      setEditedLaborData(updatedLaborData);
      return;
    }

    // Calculate editingTotal dynamically (sum of all months)
    const total = months.reduce(
      (sum, month) => sum + (Number(row[month.heading]) || 0),
      0,
    );
    row.editingTotal = Math.round(Number(total) * 100) / 100; // ensures 2 decimal places

    // Optionally, you can also keep cyPlusOne in sync
    row.cyPlusOne = Number(row.editingTotal);

    // Update the state
    setEditedLaborData(updatedLaborData);
  };
  const handleBudgetSalaryChange = (index, value) => {
    const updatedData = [...editedLaborData];
    const emp = updatedData[index];

    const budget = parseFloat(value) || 0;

    emp.budgetSalary = budget;

    setEditedLaborData(updatedData);
  };

  const parseMonthYear = (str) => {
    if (!str) return null;

    if (/\d{4}-\d{2}-\d{2}/.test(str)) {
      const d = new Date(str);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }

    const [monthStr, yearStr] = str.split("-");
    const monthIndex = new Date(`${monthStr} 1, 2000`).getMonth();
    let year = parseInt(yearStr, 10);
    if (yearStr.length === 2) year += 2000;
    return new Date(year, monthIndex, 1);
  };

  // Shared by handleActiveSalaryChange/handleUtilChange/handleMeritIncPctChange below —
  // whichever of the three inputs just changed, this re-derives the forecast-month cells (and,
  // on the budget version, the Budget Salary preview) from the row's current draft values, so
  // the grid stays internally consistent no matter which field the user touches. `meritIncPct`
  // being defined at all on `emp` is exactly this row's budget-version signal (see
  // handleEdit's initialLaborData — it's undefined for every other version):
  //  - Budget version: budgetSalary = activeSalary × (1 + meritIncPct / 100), and the monthly
  //    forecast comes from THAT merit-adjusted budgetSalary × utilPct / 100 / 12, per
  //    INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §2. This is a responsive local preview only —
  //    the guide is explicit that the persisted/authoritative values always come from the
  //    server, which recomputes the same way on save regardless of what's sent.
  //  - Every other version: unchanged pre-existing behavior — monthly forecast comes straight
  //    from activeSalary × utilPct / 100 / 12, and budgetSalary is left alone (it behaves
  //    exactly as it always has there, per the guide).
  const applyBudgetSalaryAndForecast = (emp) => {
    const activeSalary = Number(emp.activeSalary) || 0;
    const util = Number(emp.utilPct) || 0;
    const isBudgetRow =
      emp.meritIncPct !== undefined && emp.meritIncPct !== null;

    if (isBudgetRow) {
      const meritIncPct = Number(emp.meritIncPct) || 0;
      emp.budgetSalary = activeSalary * (1 + meritIncPct / 100);
    }

    const monthlyBase = isBudgetRow
      ? Number(emp.budgetSalary) || 0
      : activeSalary;
    const monthlyForecast = (monthlyBase * util) / 100 / 12;

    const forecastMonths = months.filter((m) => m.type === "Forecast");
    const start = new Date(emp.startDate);
    const employeeStartMonth = new Date(
      start.getFullYear(),
      start.getMonth(),
      1,
    );

    forecastMonths.forEach((m) => {
      const monthDate = parseMonthYear(m.expenseDate);
      emp[m.heading] =
        monthDate && monthDate >= employeeStartMonth
          ? parseFloat(monthlyForecast.toFixed(2))
          : 0;
    });
  };

  const handleActiveSalaryChange = (index, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    const updatedData = [...editedLaborData];
    const emp = updatedData[index];
    emp.activeSalary = value || 0;
    applyBudgetSalaryAndForecast(emp);
    setEditedLaborData(updatedData);
  };

  const handleUtilChange = (index, value) => {
    const updatedData = [...editedLaborData];
    const emp = updatedData[index];
    emp.utilPct = parseFloat(value) || 0;
    applyBudgetSalaryAndForecast(emp);
    setEditedLaborData(updatedData);
  };

  // Editable per row when present, no TBD-row restriction — matches how utilPct already works
  // (INDIRECT_BUDGET_VERSION_FRONTEND_GUIDE.md §2). Unlike jobTitle below, this DOES feed back
  // into a visible local preview (Budget Salary + the forecast months) via
  // applyBudgetSalaryAndForecast — a responsive preview only; the server recomputes the same
  // way, authoritatively, on save.
  const handleMeritIncPctChange = (index, value) => {
    const updatedData = [...editedLaborData];
    const emp = updatedData[index];
    emp.meritIncPct = value === "" ? "" : parseFloat(value) || 0;
    applyBudgetSalaryAndForecast(emp);
    setEditedLaborData(updatedData);
  };

  const handleJobTitleChange = (index, value) => {
    const updatedData = [...editedLaborData];
    updatedData[index].jobTitle = value;
    setEditedLaborData(updatedData);
  };

  // TBD rows only (no employeeId), same gating as jobTitle/activeSalary above. Feeds back into
  // applyBudgetSalaryAndForecast since the forecast months are zeroed out before the employee's
  // start month (see that function) — changing the start date can shift which months forecast.
  // `date` comes from CustomDatePicker (same component/picker as the "+ Add New" dialog's Start
  // Date field) as a dayjs instance, not a plain string — normalize it the same way that
  // dialog's own setDateValue does before storing it.
  const handleStartDateChange = (index, date) => {
    const updatedData = [...editedLaborData];
    const emp = updatedData[index];
    emp.startDate = formatDateBySetting(getDateMoment(date), "YYYY-MM-DD");
    applyBudgetSalaryAndForecast(emp);
    setEditedLaborData(updatedData);
  };

  // TBD rows only (no employeeId), same gating as jobTitle/activeSalary/startDate above. Also
  // updates projectName from the matching dropdown option's label, so the Project Name column
  // reflects the newly-selected project immediately, in edit mode, without waiting for a save.
  const handleProjectIdChange = (index, value) => {
    const updatedData = [...editedLaborData];
    const emp = updatedData[index];
    emp.projectId = value;

    const matched = projectOptions.find((opt) => opt.value === value);
    const fullLabel = matched?.label ?? "";
    const idPrefix = matched?.value !== undefined ? String(matched.value) : "";
    // The option's label is "<id>-<name>" (e.g. "CLIN0001-Finance System Upgrade") — strip the
    // id and separator so only the project name shows in the Project Name column.
    emp.projectName = fullLabel.startsWith(idPrefix)
      ? fullLabel.slice(idPrefix.length).replace(/^[\s:–—-]+/, "")
      : fullLabel;

    setEditedLaborData(updatedData);
  };

  const handleLaborTotalChange = (index, value) => {
    if (value !== "" && value !== "-" && !/^-?\d*\.?\d{0,2}$/.test(value))
      return;
    const updatedEdited = [...editedLaborData];
    const emp = updatedEdited[index];

    // Keep the raw "-" visible while typing, but don't redistribute the
    // forecast months off of it yet — it isn't a number, so treating it as 0
    // would zero out every forecast month before the user finishes typing a
    // negative total (same issue as the monthly-cell inputs above).
    if (value === "-") {
      emp.total = value;
      setEditedLaborData(updatedEdited);
      return;
    }

    // Validate input to 2 decimals
    const validatedValue = validateTwoDecimals(value);
    const newTotal = parseFloat(validatedValue) || 0;

    // Sum of actuals
    const sumActuals = months
      .filter((m) => m.type === "Actual")
      .reduce((sum, m) => sum + (parseFloat(emp[m.heading]) || 0), 0);

    // Forecast months
    const forecastMonths = months.filter((m) => m.type === "Forecast");
    const numForecast = forecastMonths.length;

    if (numForecast > 0) {
      const remainingTotal = newTotal - sumActuals;

      // Divide and round down to 2 decimals for all except last
      const baseValue = Math.floor((remainingTotal / numForecast) * 100) / 100;

      forecastMonths.forEach((m, i) => {
        if (i < numForecast - 1) {
          emp[m.heading] = baseValue;
        } else {
          // Last month gets the remainder to make total exact
          const totalAssigned = baseValue * (numForecast - 1);
          emp[m.heading] = parseFloat(
            (remainingTotal - totalAssigned).toFixed(2),
          );
        }
      });
    }

    emp.total = Number(newTotal);
    emp.editingTotal = Number(newTotal);

    setEditedLaborData(updatedEdited);
  };

  const handleLaborOdcTotalChange = (index, value) => {
    if (value !== "" && value !== "-" && !/^-?\d*\.?\d{0,2}$/.test(value))
      return;
    const updated = [...editedLaborODCData];
    const emp = updated[index];

    // Same "-" guard as the monthly-cell handlers: keep it visible while
    // typing, but skip redistributing the forecast months until it's a
    // real number.
    if (value === "-") {
      emp.editingTotal = value;
      setEditedLaborODCData(updated);
      return;
    }

    // Validate input to 2 decimals
    const validatedValue = validateTwoDecimals(value);
    const newTotal = parseFloat(validatedValue) || 0;

    emp.editingTotal = Number(newTotal);

    // Sum of actual months
    const sumActuals = months
      .filter((m) => m.type === "Actual")
      .reduce((sum, m) => sum + (parseFloat(emp[m.heading]) || 0), 0);

    // Forecast months
    const forecastMonths = months.filter((m) => m.type === "Forecast");
    const numForecast = forecastMonths.length;

    if (numForecast > 0) {
      const remainingTotal = newTotal - sumActuals;

      const baseValue = Math.floor((remainingTotal / numForecast) * 100) / 100;

      forecastMonths.forEach((m, i) => {
        if (i < numForecast - 1) {
          emp[m.heading] = baseValue;
        } else {
          const totalAssigned = baseValue * (numForecast - 1);
          emp[m.heading] = parseFloat(
            (remainingTotal - totalAssigned).toFixed(2),
          );
        }
      });
    }

    setEditedLaborODCData(updated);
  };

  const handleLaborIndirectTotalChange = (index, value) => {
    if (value !== "" && value !== "-" && !/^-?\d*\.?\d{0,2}$/.test(value))
      return;
    const updated = [...editedIndirectExpensesData];
    const emp = updated[index];

    // Same "-" guard as the monthly-cell handlers: keep it visible while
    // typing, but skip redistributing the forecast months until it's a
    // real number — otherwise typing "-" here briefly zeroes every forecast
    // month before the rest of the negative total is typed.
    if (value === "-") {
      emp.editingTotal = value;
      setEditedIndirectExpensesData(updated);
      return;
    }

    const newTotal = parseFloat(value) || 0;

    emp.editingTotal = Number(newTotal);

    const sumActuals = months
      .filter((m) => m.type === "Actual")
      .reduce((sum, m) => sum + (parseFloat(emp[m.heading]) || 0), 0);

    const forecastMonths = months.filter((m) => m.type === "Forecast");

    const forecastValue =
      forecastMonths.length > 0
        ? (newTotal - sumActuals) / forecastMonths.length
        : 0;

    forecastMonths.forEach((m) => {
      emp[m.heading] = forecastValue;
    });

    setEditedIndirectExpensesData(updated);
  };

  const handleInputChange = (index, field, value) => {
    const updatedData = [...editedLaborODCData];
    updatedData[index][field] = value;

    // If we are updating the annual target, we also need to update the monthly fields
    if (field === "annualTarget") {
      const annualTarget = parseFloat(value);
      if (!isNaN(annualTarget)) {
        const newMonthlyValue = annualTarget / 12;
        months.forEach((month) => {
          updatedData[index][month] = newMonthlyValue;
        });
      } else {
        months.forEach((month) => {
          updatedData[index][month] = 0;
        });
      }
    }

    // If costPerEmployee or numberOfEmployee changes, recalculate annualTarget and update months
    if (field === "costPerEmployee" || field === "numberOfEmployee") {
      const costPerEmp = parseFloat(updatedData[index].costPerEmployee);
      const numOfEmp = parseFloat(updatedData[index].numberOfEmployee);
      let annualTarget = 0;

      if (!isNaN(costPerEmp) && !isNaN(numOfEmp)) {
        annualTarget = costPerEmp * numOfEmp;
        updatedData[index].annualTarget = annualTarget.toString();
      } else if (!isNaN(costPerEmp) && isNaN(numOfEmp)) {
        annualTarget = costPerEmp;
        updatedData[index].annualTarget = costPerEmp.toString();
      } else {
        updatedData[index].annualTarget = "";
      }

      // Update monthly fields if annualTarget is valid
      if (!isNaN(annualTarget) && annualTarget > 0) {
        const newMonthlyValue = annualTarget / 12;
        months.forEach((month) => {
          updatedData[index][month] = newMonthlyValue;
        });
      } else {
        months.forEach((month) => {
          updatedData[index][month] = 0;
        });
      }
    }

    setEditedLaborODCData(updatedData);
  };

  // Handle input changed in project per month salary laborodc
  const handleLaborODCMonthlySalaryChange = (rowIndex, monthHeading, value) => {
    if (value !== "" && value !== "-" && !/^-?\d*\.?\d{0,2}$/.test(value))
      return;
    const updated = [...editedLaborODCData];
    const row = updated[rowIndex];

    // Validate input to 2 decimals
    //const validatedValue = validateTwoDecimals(value);

    row[monthHeading] = value;

    // A lone "-" is just the start of typing a negative number — keep it in
    // the cell, but skip recalculating editingTotal until a real number
    // follows (same reasoning as handleLaborMonthlySalaryChange above).
    if (value === "-") {
      setEditedLaborODCData(updated);
      return;
    }

    // Update editingTotal dynamically
    const total = months.reduce(
      (sum, month) => sum + (Number(row[month.heading]) || 0),
      0,
    );
    row.editingTotal = Math.round(Number(total) * 100) / 100; // ensures 2 decimal places
    setEditedLaborODCData(updated);
  };

  const calculateTotalByProject = (id) => {
    if (isEdit) {
      const dataItem = editedLaborData[id];
      console.log(dataItem);
      if (dataItem) {
        if (dataItem.editingTotal !== undefined) {
          return Number(dataItem.editingTotal) || 0;
        } else {
          return months.reduce(
            (sum, m) => sum + (Number(dataItem[m.heading]) || 0),
            0,
          );
        }
      }
    } else {
      const dataItem = laborData[id];
      if (dataItem)
        return months.reduce(
          (sum, m) =>
            sum +
            (Number(dataItem[m.heading.split("-")[0].toLowerCase()]) || 0),
          0,
        );
    }
    return 0;
  };

  const calculateODCTotalByProject = (id) => {
    if (isEdit) {
      const dataItem = editedLaborODCData[id];
      if (dataItem) {
        if (dataItem.editingTotal !== undefined) {
          return Number(dataItem.editingTotal) || 0;
        } else {
          return months.reduce(
            (sum, m) => sum + (parseFloat(dataItem[m.heading]) || 0),
            0,
          );
        }
      }
    } else {
      const dataItem = laborODCData[id];
      if (dataItem)
        return months.reduce(
          (sum, m) =>
            sum +
            (parseFloat(dataItem[m.heading.split("-")[0].toLowerCase()]) || 0),
          0,
        );
    }
    return 0;
  };

  const calculateIndirectTotalByProject = (id) => {
    if (isEdit) {
      const dataItem = editedIndirectExpensesData[id];
      if (dataItem) {
        // If there is an edited total, return it directly
        if (dataItem.editingTotal !== undefined) {
          return Number(dataItem.editingTotal) || 0;
        } else {
          return months.reduce(
            (sum, month) => sum + (parseFloat(dataItem[month.heading]) || 0),
            0,
          );
        }
      }
    } else {
      const dataItem = IndirectExpensesData[id];
      if (dataItem) {
        return months.reduce(
          (sum, month) =>
            sum +
            (parseFloat(dataItem[month.heading.split("-")[0].toLowerCase()]) ||
              0),
          0,
        );
      }
    }
    return 0;
  };

  //   IndirectExperience
  // const handleInputExpenses = (index, field, value) => {
  //   const updatedData = [...editedIndirectExpensesData];
  //   updatedData[index][field] = value;

  //   if (field === "costPerEmployee" || field === "numberOfEmployee") {
  //     const costPerEmp = parseFloat(updatedData[index].costPerEmployee);
  //     const numOfEmp = parseFloat(updatedData[index].numberOfEmployee);

  //     if (!isNaN(costPerEmp) && !isNaN(numOfEmp)) {
  //       updatedData[index].annualTarget = (costPerEmp * numOfEmp).toString();
  //     } else if (!isNaN(costPerEmp) && isNaN(numOfEmp)) {
  //       updatedData[index].annualTarget = costPerEmp.toString();
  //     } else {
  //       updatedData[index].annualTarget = ""; // Reset Annual Target
  //     }
  //   }
  //   if (field === "annualTarget") {
  //     const updatedIndirectExpensesData = [...IndirectExpensesData]; // Clone to prevent mutation
  //     if (updatedIndirectExpensesData[index]) {
  //       const annualTargetValue = value ? parseFloat(value) : null;
  //       updatedIndirectExpensesData[index].annualTarget = annualTargetValue;
  //       if (annualTargetValue !== null && annualTargetValue !== "") {
  //         const monthlyAmount = Math.floor(annualTargetValue / 12);
  //         const remainder = annualTargetValue % 12; // Handle remainder

  //         monthsArray.forEach((month, i) => {
  //           updatedIndirectExpensesData[index][month] =
  //             monthlyAmount + (i < remainder ? 1 : 0);
  //         });
  //       }
  //     }
  //     setIndirectExpensesData(updatedIndirectExpensesData);
  //   }
  //   setEditedIndirectExpensesData(updatedData);
  // };
  const handleInputExpenses = (index, field, value) => {
    const updatedData = [...editedIndirectExpensesData];
    updatedData[index][field] = value;

    // If we are updating the annual target, we also need to update the monthly fields
    if (field === "annualTarget") {
      const annualTarget = parseFloat(value);
      // Update the months based on the new annual target
      if (!isNaN(annualTarget)) {
        const newMonthlyValue = annualTarget / 12;
        // Update the months to reflect the new monthly target
        months.forEach((month) => {
          updatedData[index][month] = newMonthlyValue;
        });
      } else {
        // If the annual target is invalid, reset months to 0
        months.forEach((month) => {
          updatedData[index][month] = 0;
        });
      }
    }

    // If costPerEmployee or numberOfEmployee changes, recalculate annualTarget
    // If costPerEmployee or numberOfEmployee changes, recalculate annualTarget and update months
    if (field === "costPerEmployee" || field === "numberOfEmployee") {
      const costPerEmp = parseFloat(updatedData[index].costPerEmployee);
      const numOfEmp = parseFloat(updatedData[index].numberOfEmployee);
      let annualTarget = 0;

      if (!isNaN(costPerEmp) && !isNaN(numOfEmp)) {
        annualTarget = costPerEmp * numOfEmp;
        updatedData[index].annualTarget = annualTarget.toString();
      } else if (!isNaN(costPerEmp) && isNaN(numOfEmp)) {
        annualTarget = costPerEmp;
        updatedData[index].annualTarget = costPerEmp.toString();
      } else {
        updatedData[index].annualTarget = "";
      }

      // Update monthly fields if annualTarget is valid
      if (!isNaN(annualTarget) && annualTarget > 0) {
        const newMonthlyValue = annualTarget / 12;
        months.forEach((month) => {
          updatedData[index][month] = newMonthlyValue;
        });
      } else {
        months.forEach((month) => {
          updatedData[index][month] = 0;
        });
      }
    }

    setEditedIndirectExpensesData(updatedData);
  };

  // Calculate the monthly project salary based on the project salary (evenly distributed)
  const handleIndirectMonthlyExpenses = (rowIndex, monthHeading, value) => {
    if (value !== "" && value !== "-" && !/^-?\d*\.?\d{0,2}$/.test(value))
      return;
    const updatedData = [...editedIndirectExpensesData];
    const row = updatedData[rowIndex];

    // Keep the raw typed value (including a bare "-") so the user can type a
    // negative number one keystroke at a time, same as the Labor/Non-Labor
    // monthly inputs above.
    row[monthHeading] = value;

    // Skip recalculating editingTotal while the cell is just "-" — not a
    // number yet. Resumes below as soon as a digit follows.
    if (value === "-") {
      setEditedIndirectExpensesData(updatedData);
      return;
    }

    row.editingTotal = months.reduce(
      (sum, month) => sum + (parseFloat(row[month.heading]) || 0),
      0,
    );

    setEditedIndirectExpensesData(updatedData);
  };

  const calculateMonthlySalary = (odc, monthObj) => {
    const month = monthObj?.heading;

    if (typeof month !== "string" || !month.includes("-")) {
      console.error("Invalid month format:", month);
      return 0;
    }

    const targetMonth = month.split("-")[0].toLowerCase();

    // `|| 0`: same NaN-poisoning risk as the edit-mode totals above if this
    // row's stored value is ever non-numeric — this feeds a .reduce() in
    // calculateLaborMonthlyTotal's non-edit branch with no guard of its own.
    return Number(odc[targetMonth]) || 0;
  };

  const calculateMonthlyTarget = (odc, monthObj) => {
    const month = monthObj?.heading;

    if (typeof month !== "string" || !month.includes("-")) {
      console.error("Invalid month format:", month);
      return 0;
    }

    const targetMonth = month.split("-")[0].toLowerCase();
    return Number(odc[targetMonth]) || 0;
  };

  const calculateMonthlyExpenses = (odc, monthObj) => {
    const monthStr = monthObj?.heading;

    if (typeof monthStr !== "string" || !monthStr.includes("-")) {
      console.error("Invalid month format:", monthStr);
      return 0;
    }

    const targetMonth = monthStr.split("-")[0].toLowerCase();
    // Number(...) matters: odc[targetMonth] can be a raw string, and
    // `string || 0` returns the string itself (truthy) rather than
    // converting it — the caller's `total + currentValue` then silently
    // string-concatenates instead of adding, corrupting the running total.
    return Number(odc[targetMonth]) || 0;
  };

  const calculateTotalProjectSalary = () => {
    let total = 0;
    laborData.forEach((odc, i) => {
      total += calculateTotalByProject(i);
    });
    return total;
  };

  const calculateTotalAnnualTarget = () => {
    let total = 0;
    laborODCData.forEach((odc, i) => {
      total += calculateODCTotalByProject(i);
    });
    return total;
  };

  //IndirectExpenses

  const calculateTotalAnnualExpenses = () => {
    let total = 0;
    IndirectExpensesData.forEach((odc, i) => {
      total += calculateIndirectTotalByProject(i);
    });
    return total;
  };

  // const calculateTotalAnnualExpenses = () => {
  //   if (isEdit) {
  //     return editedIndirectExpensesData.reduce((total, expense) => {
  //       const annualTarget = parseFloat(expense.annualTarget);
  //       return !isNaN(annualTarget) ? total + annualTarget : total;
  //     }, 0);
  //   }
  //   return IndirectExpensesData.reduce((total, expense) => {
  //     const annualTarget = parseFloat(expense.annualTarget);
  //     return !isNaN(annualTarget) ? total + annualTarget : total;
  //   }, 0);
  // };

  // Calculate the monthly total from all employees' Project Salaries
  const calculateLaborMonthlyTotal = (monthObj) => {
    const month = monthObj?.heading;
    if (isEdit) {
      // In editing mode, calculate monthly total from the edited data.
      // `|| 0` matters here: a cell mid-edit can briefly hold a non-numeric
      // string like "-" (the user typing a negative number one keystroke at
      // a time) or "". Number() on that is NaN, and NaN + anything is NaN —
      // without this fallback, one such row poisons the whole reduce, and
      // every other row's total displays as "$0" until the input resolves.
      return laborData.reduce((total, odc, i) => {
        const currentValue = Number(editedLaborData[i][month]) || 0; // Use extracted month string
        return total + currentValue;
      }, 0);
    } else {
      return laborData.reduce((total, odc) => {
        const monthlyTarget = calculateMonthlySalary(odc, monthObj); // still pass monthObj
        return total + monthlyTarget;
      }, 0);
    }
  };

  //Calculate the monthly total laborODC
  const calculateMonthlyLaborODCTotal = (monthObj) => {
    const month = monthObj?.heading;
    if (isEdit) {
      // In editing mode, calculate monthly total from the edited data.
      // `|| 0` guards the same mid-edit "-"/"" NaN-poisoning issue as
      // calculateLaborMonthlyTotal above.
      return laborODCData.reduce((total, odc, i) => {
        const currentValue = Number(editedLaborODCData[i][month]) || 0; // Use edited value or fallback to 0 if not available
        return total + currentValue;
      }, 0);
    } else {
      return laborODCData.reduce((total, odc) => {
        // Use calculateMonthlyTarget for the specified month
        const monthlyTarget = calculateMonthlyTarget(odc, monthObj); // Pass month to the target calculation
        return total + monthlyTarget; // Add the calculated target for the month
      }, 0);
    }
  };

  //IndirectExpenses

  const calculateMonthlyIndirectExpensesTotal = (monthObj) => {
    const month = monthObj?.heading;
    if (isEdit) {
      // In editing mode, calculate monthly total from the edited data.
      // Number(...) is required here: the cell holds a raw typed string
      // (including, mid-edit, "-"), and `string || 0` only replaces falsy
      // values — any non-empty string (even a normal "500") passes through
      // unconverted, and `total + currentValue` then string-concatenates
      // instead of summing once a string reaches a numeric accumulator.
      return IndirectExpensesData.reduce((total, odc, i) => {
        const currentValue = Number(editedIndirectExpensesData[i][month]) || 0;
        return total + currentValue;
      }, 0);
    } else {
      return IndirectExpensesData.reduce((total, odc) => {
        // Use calculateMonthlyTarget for the specified month
        const monthlyTarget = calculateMonthlyExpenses(odc, monthObj); // Pass month to the target calculation
        return total + monthlyTarget; // Add the calculated target for the month
      }, 0);
    }
  };

  // below total table
  const LaborDetail = ["Total", "Labor", "Non Labor Cost"];

  const resetDropDowns = () => {
    setSelectedSubDepartment("");
    setSelectedYear("");
    setSelectedVersion("");
  };

  const refreshDropdownsPreserveSelection = async () => {
    const prevDept = selectedDepartment;
    const prevSub = selectedSubDepartment;
    const prevYear = selectedYear;
    const prevVersion = selectedVersion;

    try {
      const response = await getIndirectDropdownsAPI();
      if (response.status === 200) {
        const departmentData = response.data.map((department) => ({
          ...department,
          value: department.department,
          label: department.department,
        }));

        setDepartmentList(departmentData);

        if (prevDept) setSelectedDepartment(prevDept);
        if (prevSub) setSelectedSubDepartment(prevSub);
        if (prevYear) setSelectedYear(prevYear);
        if (prevVersion) setSelectedVersion(prevVersion);
      } else if (response.status === 400) {
        setDepartmentList([]);
        setYears([]);
      }
    } catch (err) {}
  };

  const resetData = () => {
    setLaborData([]);
    setLaborAdjustments([]);
    setLaborODCData([]);
    setEditedLaborData([]);
    setIndirectExpensesData([]);
    setEditedIndirectExpensesData([]);
  };

  const handleDownloadIndirectLabor = () => {
    setDownloading(true);
    const versionName =
      versionList.find((v) => v.value === selectedVersion)?.label ??
      selectedVersion;
    downloadIndirectLaborAPI({
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      financialYear: selectedYear,
      version: selectedVersion,
    }).then((res) => {
      setDownloading(false);
      if (res?.type) {
        downloadFile(
          res,
          `indirect_${selectedDepartment}_${selectedYear}_${versionName}`,
        );
      }
    });
  };

  const toggleLaborColumn = (key) =>
    setHiddenLaborCols((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
      saveHiddenColumns(LABOR_HIDDEN_COLS_KEY, next);
      return next;
    });
  const resetLaborColumns = () => {
    setHiddenLaborCols([]);
    saveHiddenColumns(LABOR_HIDDEN_COLS_KEY, []);
  };

  const toggleLaborODCColumn = (key) =>
    setHiddenLaborODCCols((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
      saveHiddenColumns(LABOR_ODC_HIDDEN_COLS_KEY, next);
      return next;
    });
  const resetLaborODCColumns = () => {
    setHiddenLaborODCCols([]);
    saveHiddenColumns(LABOR_ODC_HIDDEN_COLS_KEY, []);
  };

  const fetchPlanFiles = () => {
    if (!selectedVersion) return;
    setPlanFilesLoading(true);
    getIndirectPlanFilesAPI(selectedVersion, selectedDepartment).then((res) => {
      setPlanFilesLoading(false);
      if (res?.status === 200) {
        setPlanFiles(res.data || []);
      } else {
        // The list API 400s once there are no files left for this version instead of
        // returning 200 with an empty array — treat that as "no attachments" rather than
        // leaving the just-deleted file stuck on screen from the previous fetch.
        setPlanFiles([]);
      }
    });
  };

  // Blank template for the "Upload Data" flow above — read-only, no version gate, just the
  // financial year. See APIIntegrationFiles/INDIRECT_NONLABOR_UPLOAD_FRONTEND_GUIDE.md §3.
  const handleDownloadTemplate = () => {
    setDownloading(true);
    downloadIndirectNonLaborTemplateAPI(selectedYear)
      .then((res) => {
        setDownloading(false);
        if (res?.isFile) {
          downloadFile(res.blob, `IndirectNonLaborTemplate_${selectedYear}`);
        } else {
          setEmptyErrMsg(true);
          setMsgType("error");
          setMsgText(
            res?.message ||
              "Unable to download the template. Please try again.",
          );
        }
      })
      .catch(() => {
        setDownloading(false);
        setEmptyErrMsg(true);
        setMsgType("error");
        setMsgText("Something went wrong while downloading the template.");
      });
  };

  const handleOpenUploadMenu = (event) => {
    setUploadMenuAnchor(event.currentTarget);
  };

  const handleCloseUploadMenu = () => {
    setUploadMenuAnchor(null);
  };

  const handleSelectUploadMode = (mode) => {
    setUploadMenuAnchor(null);
    setUploadMode(mode);
    setPlanFileError("");
    setShowUploadDrawer(true);
  };

  const handleOpenPlanFiles = () => {
    setPlanFileError("");
    setShowPlanFilesDialog(true);
    fetchPlanFiles();
  };

  // Refresh the Labor and Labor ODC tables after a successful "Upload Data" import — same two
  // calls the initial load effect above makes (get-indirect-labor, get-labor-odc), so the grids
  // reflect the values the upload just wrote.
  const refetchIndirectTableData = () => {
    getIndirectLaborAPI({
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      financialYear: selectedYear,
      version: selectedVersion,
    }).then((response) => {
      if (response.status === 200) {
        setLaborData(response.data.laborDetails);
        setLaborAdjustments(response.data.adjustments);
        setLaborTotals(response.data.totals || null);
      }
    });

    getIndirectLaborODCAPI({
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      financialYear: selectedYear,
      version: selectedVersion,
    }).then((response) => {
      if (response.status === 200) {
        setLaborODCData(response.data);
        setEditedLaborData(response.data);
      }
    });
  };

  // Upload only succeeds while `selectedVersion` is currently editable — the API itself
  // enforces that (400 otherwise), so no client-side "is this the budget version" check is
  // added here, matching the same "let the backend decide" convention used for Labor edits.
  const handleUploadPlanFile = (formData) => {
    formData.append("version", selectedVersion);
    if (selectedDepartment) formData.append("department", selectedDepartment);
    if (selectedSubDepartment)
      formData.append("subDepartment", selectedSubDepartment);

    setPlanFileError("");
    setPlanFileUploading(true);

    if (uploadMode === "data") {
      // Success comes back as the binary result workbook (not JSON); errors use the normal
      // JSON envelope. See APIIntegrationFiles/INDIRECT_NONLABOR_UPLOAD_FRONTEND_GUIDE.md.
      uploadIndirectNonLaborDataAPI(formData)
        .then((res) => {
          setPlanFileUploading(false);
          if (res?.isFile) {
            downloadFile(res.blob, "nonLaborUploadResult");
            setShowUploadDrawer(false);
            refetchIndirectTableData();
          } else {
            // Error envelope is { data: { status, message } }, not top-level — same wrapper
            // used across this app's other APIs. Shown via the ActionMessages toast only — no
            // inline drawer error for this mode.
            const errorMessage =
              res?.data?.message ||
              res?.message ||
              "Unable to upload the file. Please check the file and try again.";
            setEmptyErrMsg(true);
            setMsgType("error");
            setMsgText(errorMessage);
          }
        })
        .catch(() => {
          setPlanFileUploading(false);
          setEmptyErrMsg(true);
          setMsgType("error");
          setMsgText("Something went wrong during the upload.");
        });
      return;
    }

    uploadIndirectPlanFileAPI(formData)
      .then((res) => {
        setPlanFileUploading(false);
        if (res?.status === 200) {
          setShowUploadDrawer(false);
        } else {
          setPlanFileError(
            res?.message ||
              "Unable to upload the file. Please check the file and try again.",
          );
        }
      })
      .catch(() => {
        setPlanFileUploading(false);
        setPlanFileError("Something went wrong during the upload.");
      });
  };

  const handleDownloadPlanFile = (file) => {
    downloadIndirectPlanFileAPI(file.id).then((res) => {
      if (res?.type) {
        downloadFile(res, file.fileName);
      }
    });
  };

  // GET /financial/indirect/indirect-dropdown now flags the budget version directly
  // (`isBudget`), carried through into versionList above — no more guessing from
  // `versionName`. Used to scope the budget-only affordances (Merit Increase %/Job Title on
  // Add New, and the plan-file Upload/Attachments icons) to the version they actually apply
  // to, rather than showing them for any open/editable version.
  const isBudgetVersion = Boolean(
    versionList.find((v) => v.value === selectedVersion)?.isBudget,
  );
  // Separate per-version flag (also from GET .../indirect-dropdown) — whether the budget
  // version is currently in its editable window, independent of the general
  // versionStatus/accessType/department.isEditable checks used everywhere else. Used to widen
  // (never narrow) the Edit-icon gate below: the budget version can be editable through this
  // flag even when one of those other checks wouldn't otherwise allow it.
  const isEditableBudget = Boolean(
    versionList.find((v) => v.value === selectedVersion)?.isEditableBudget,
  );

  return (
    <div>
      <Grid container spacing={1} style={{ marginTop: "16px" }}>
        <Grid item lg={2}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: "7px",
              paddingRight: "4px",
            }}
          >
            Select Department
          </div>
          <Select
            showSearch
            mode="single"
            style={{ width: "230px", zIndex: 9, marginBottom: "12px" }}
            placeholder="Search to Select"
            showArrow
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label.toLowerCase() ?? "").includes(input.toLowerCase())
            }
            value={selectedDepartment}
            options={departmentList}
            onChange={(val) => {
              setIsEdit(false);
              setSelectedDepartment(val);
              resetDropDowns();
              resetData();
            }}
          />
        </Grid>
        <Grid item lg={2}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: "7px",
              paddingRight: "4px",
            }}
          >
            Select Sub Department
          </div>
          <Select
            showSearch
            mode="single"
            style={{ width: "230px", zIndex: 9, marginBottom: "12px" }}
            placeholder="Search to Select"
            showArrow
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label.toLowerCase() ?? "").includes(input.toLowerCase())
            }
            options={subDepartmentList}
            value={selectedSubDepartment}
            onChange={(val) => {
              setSelectedSubDepartment(val);
              setIsEdit(false);
              resetData();
            }}
          />
        </Grid>
        <Grid item lg={2}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: "7px",
              paddingRight: "4px",
            }}
          >
            Select Year
          </div>
          <Select
            showSearch
            mode="single"
            style={{ width: "230px", zIndex: 9, marginBottom: "12px" }}
            placeholder="Search to Select"
            showArrow
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label.toLowerCase() ?? "").includes(input.toLowerCase())
            }
            options={years}
            value={selectedYear}
            onChange={(val) => {
              setIsEdit(false);
              setSelectedYear(val);
              resetData();
            }}
          />
        </Grid>
        <Grid item lg={2}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: "7px",
              paddingRight: "4px",
            }}
          >
            Select Version
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Select
              showSearch
              mode="single"
              style={{ width: "230px", zIndex: 9, marginBottom: "12px" }}
              placeholder="Search to Select"
              showArrow
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label.toLowerCase() ?? "").includes(
                  input.toLowerCase(),
                )
              }
              options={versionList}
              value={selectedVersion}
              onChange={(val) => {
                setSelectedVersion(val);
                setIsEdit(false);
                resetData();
                setVersionStatus(
                  versionList.find((v) => v.value === val)?.status || "",
                );
              }}
            />
          </div>
        </Grid>
        <Grid item lg={4} style={{ textAlign: "right", paddingTop: "20px" }}>
          <>
            {selectedDepartment &&
              selectedSubDepartment &&
              selectedYear &&
              selectedVersion !== "" &&
              isBudgetVersion &&
              isEditableBudget && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    marginTop: "8px",
                    marginLeft: "8px",
                    borderRadius: "6px",
                    background: isEdit ? "#ccc" : "#0070D2",
                    boxShadow: "0px 1px 3px rgba(0,0,0,0.161)",
                    cursor: isEdit ? "default" : "pointer",
                    float: "right",
                  }}
                  title={!isEdit ? "Attachments" : ""}
                  onClick={isEdit ? undefined : handleOpenPlanFiles}
                >
                  <AttachFileIcon style={{ color: "#fff", fontSize: 16 }} />
                </span>
              )}
            {/* Icons float right, so DOM order below is right-to-left: Attachments (rightmost,
                already rendered above), Download, Upload (menu), Download Template (leftmost). */}
            {selectedDepartment &&
              selectedSubDepartment &&
              selectedYear &&
              selectedVersion !== "" && (
                <span
                  style={{
                    cursor: isEdit ? "default" : "pointer",
                    marginBottom: "12px",
                    float: "right",
                  }}
                  title={!isEdit ? "Download" : ""}
                  onClick={isEdit ? undefined : handleDownloadIndirectLabor}
                >
                  <DownloadIcon
                    background={isEdit ? "#ccc" : "#0070D2"}
                    color="#fff"
                  />
                </span>
              )}
            {/* Plan files only ever apply to the budget version (`isBudget`, from
                GET .../indirect-dropdown), and only while it currently IS the editable budget
                version (`isEditableBudget`) — the API 400s on a locked/expired budget version,
                so both Attachments and Upload hide together once the version stops being
                editable. */}
            {selectedDepartment &&
              selectedSubDepartment &&
              selectedYear &&
              selectedVersion !== "" &&
              isBudgetVersion &&
              isEditableBudget && (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      cursor: isEdit ? "default" : "pointer",
                      marginBottom: "12px",
                      float: "right",
                      marginLeft: "0px",
                    }}
                    title={!isEdit ? "Upload" : ""}
                    onClick={isEdit ? undefined : handleOpenUploadMenu}
                  >
                    <UploadIcon
                      background={isEdit ? "#ccc" : "#0070D2"}
                      color="#fff"
                    />
                  </span>
                  <Menu
                    anchorEl={uploadMenuAnchor}
                    open={Boolean(uploadMenuAnchor)}
                    onClose={handleCloseUploadMenu}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem onClick={() => handleSelectUploadMode("data")}>
                      Upload Data
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleSelectUploadMode("attachment")}
                    >
                      Upload Attachment
                    </MenuItem>
                  </Menu>
                </>
              )}
            {selectedDepartment &&
              selectedSubDepartment &&
              selectedYear &&
              selectedVersion !== "" &&
              isBudgetVersion &&
              isEditableBudget && (
                <span
                  style={{
                    cursor: isEdit ? "default" : "pointer",
                    marginBottom: "0",
                    marginTop: "8px",
                    float: "right",
                  }}
                  title={!isEdit ? "Download Template" : ""}
                  onClick={isEdit ? undefined : handleDownloadTemplate}
                >
                  <DownloadTemplateIcon color={isEdit ? "#ccc" : "#0070D2"} />
                </span>
              )}
          </>
        </Grid>
      </Grid>
      {departmentList.filter((val) => val.department === selectedDepartment)[0]
        ?.isEditable &&
        ((selectedSubDepartment !== "" &&
          versionStatus.toLowerCase() === "open" &&
          localStorage.getItem("accessType") === "RW") ||
          isEditableBudget) && (
        <Grid item lg={12}>
          <Box display="flex" flexDirection="row" style={{ float: "right" }}>
            <Box>
              {!isEdit ? (
                <div className={classes.editBtn} onClick={handleEdit}>
                  <EditIcon className={classes.editIcon} />
                  <span className={classes.editText}> Edit </span>
                </div>
              ) : (
                <>
                  <span className={classes.saveBtn} onClick={handleSave}>
                    {" "}
                    Save{" "}
                  </span>

                  <span className={classes.cancelBtn} onClick={handleCancel}>
                    {" "}
                    Cancel{" "}
                  </span>
                </>
              )}
            </Box>
          </Box>
        </Grid>
      )}
      {(laborData?.length > 0 ||
        laborODCData?.length > 0 ||
        IndirectExpensesData?.length > 0) && (
        <>
          <div className={classes.tableTitle} style={{ marginTop: "16px" }}>
            Indirect Forecast
          </div>
          <div>
            <Box display="flex" flexDirection="row">
              <Box
                style={{
                  boxShadow: "0px 3px 6px #00000029",
                  marginRight: "8px",
                }}
              >
                <table
                  className="firstTableFin"
                  cellPadding={0}
                  cellSpacing={0}
                >
                  <thead>
                    <tr>
                      <th style={{ minWidth: "720px", maxWidth: "720px" }}></th>
                    </tr>
                  </thead>
                  <tbody style={{ borderRadius: "10px" }}>
                    {LaborDetail.map((item, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: "center" }}>
                          {i == 0 ? <strong>{item}</strong> : item}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <Box
                ref={PlanningIndirectNonLabourRef}
                style={{
                  overflowX: "scroll",
                  boxShadow: "0px 3px 6px #00000029",
                }}
                onScroll={() => handleScrollTable("PlanningIndirectNonLabour")}
              >
                <Box display="flex" flexDirection="row">
                  <Box
                    style={{
                      boxShadow: "0px 3px 6px #00000029",
                      marginRight: "8px",
                    }}
                  >
                    <table
                      className="firstTableFin"
                      cellPadding={0}
                      cellSpacing={0}
                    >
                      <thead>
                        <tr>
                          {months.map((month) => (
                            <th
                              key={month.heading}
                              className="centerAlignedHeaderLeftBorder"
                              style={{
                                minWidth: "90px",
                                maxWidth: "90px",
                                height: "40px",
                              }}
                            >
                              {month.heading}
                            </th>
                          ))}
                          <th
                            rowSpan={2}
                            className="centerAlignedHeaderLeftBorder"
                            style={{
                              minWidth: "90px",
                              maxWidth: "90px",
                            }}
                          >
                            Total
                          </th>
                        </tr>
                        <tr>
                          {months.map((month, i) => (
                            <th
                              key={i}
                              className={`${
                                month.type === "Actual"
                                  ? "actualsHeaderCell"
                                  : "forecastHeaderCell"
                              } leftAlignOverride`}
                              style={{ height: "40px" }}
                            >
                              {month.type === "Actual" ? "Actuals" : "Forecast"}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {months.map((item, i) => (
                            <td className={classes.centerAlignedData}>
                              <strong>
                                {convertValuesFM(
                                  calculateLaborMonthlyTotal(item) +
                                    calculateMonthlyLaborODCTotal(item) +
                                    calculateMonthlyIndirectExpensesTotal(item),
                                )}
                              </strong>
                            </td>
                          ))}
                          <td className={classes.centerAlignedData}>
                            <strong>
                              {convertValuesFM(
                                calculateTotalProjectSalary() +
                                  calculateTotalAnnualTarget() +
                                  calculateTotalAnnualExpenses(),
                              )}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          {months.map((item, i) => (
                            <td className={classes.centerAlignedData}>
                              {convertValuesFM(
                                calculateLaborMonthlyTotal(item),
                              )}
                            </td>
                          ))}
                          <td className={classes.centerAlignedData}>
                            <strong>
                              {convertValuesFM(calculateTotalProjectSalary())}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          {months.map((item, i) => (
                            <td className={classes.centerAlignedData}>
                              {convertValuesFM(
                                calculateMonthlyLaborODCTotal(item),
                              )}
                            </td>
                          ))}
                          <td className={classes.centerAlignedData}>
                            <strong>
                              {convertValuesFM(calculateTotalAnnualTarget())}
                            </strong>
                          </td>
                        </tr>
                        <tr></tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>
            </Box>
          </div>

          <RenderLabor
            classes={classes}
            laborData={laborData}
            months={months}
            isEdit={isEdit}
            calculateMonthlySalary={calculateMonthlySalary}
            editedLaborData={editedLaborData}
            handleLaborSalaryChange={handleLaborSalaryChange}
            handleLaborMonthlySalaryChange={handleLaborMonthlySalaryChange}
            handleLaborTotalChange={handleLaborTotalChange}
            handleScrollTable={handleScrollTable}
            LaborRef={LaborRef}
            filteredLabor={filteredLabor}
            setFilteredLabor={setFilteredLabor}
            calculateTotalByProject={calculateTotalByProject}
            handleAddNewLabor={handleAddNewLabor}
            onClickLaborDetails={onClickLaborDetails}
            versionStatus={versionStatus}
            handleBudgetSalaryChange={handleBudgetSalaryChange}
            handleActiveSalaryChange={handleActiveSalaryChange}
            handleUtilChange={handleUtilChange}
            handleMeritIncPctChange={handleMeritIncPctChange}
            handleJobTitleChange={handleJobTitleChange}
            handleStartDateChange={handleStartDateChange}
            handleProjectIdChange={handleProjectIdChange}
            projectOptions={projectOptions}
            selectedYear={selectedYear}
            dateFormat={dateFormat}
            onDeleteIconClick={onDeleteIconClick}
            isEditable={
              departmentList.filter(
                (val) => val.department === selectedDepartment,
              )[0].isEditable
            }
            isEditableBudget={isEditableBudget}
            departmentName={selectedDepartment}
            laborTotals={laborTotals}
            showMeritIncPct={laborData.some(
              (row) => row.meritIncPct !== undefined,
            )}
            hiddenColumns={hiddenLaborCols}
            onToggleColumn={toggleLaborColumn}
            onResetColumns={resetLaborColumns}
          />

          <RenderLaborODC
            classes={classes}
            laborODCData={laborODCData}
            months={months}
            isEdit={isEdit}
            editedLaborODCData={editedLaborODCData}
            handleLaborODCMonthlySalaryChange={
              handleLaborODCMonthlySalaryChange
            }
            handleInputChange={handleInputChange}
            calculateMonthlyTarget={calculateMonthlyTarget}
            calculateMonthlyLaborODCTotal={calculateMonthlyLaborODCTotal}
            calculateTotalAnnualTarget={calculateTotalAnnualTarget}
            handleScrollTable={handleScrollTable}
            LaborODCRef={LaborODCRef}
            filteredlaborODCData={filteredlaborODCData}
            setFilteredLaborODCData={setFilteredLaborODCData}
            calculateODCTotalByProject={calculateODCTotalByProject}
            handleLaborOdcTotalChange={handleLaborOdcTotalChange}
            handleAddNewNonLabor={handleAddNewNonLabor}
            onClickNonLaborDetails={onClickNonLaborDetails}
            versionStatus={versionStatus}
            onDeleteNonLaborClick={onDeleteNonLaborClick}
            isEditable={
              departmentList.filter(
                (val) => val.department === selectedDepartment,
              )[0].isEditable
            }
            isEditableBudget={isEditableBudget}
            departmentName={selectedDepartment}
            hiddenColumns={hiddenLaborODCCols}
            onToggleColumn={toggleLaborODCColumn}
            onResetColumns={resetLaborODCColumns}
          />

          {/* <RenderIndirectExpenses
            classes={classes}
            IndirectExpensesData={IndirectExpensesData}
            months={months}
            isEdit={isEdit}
            editedIndirectExpensesData={editedIndirectExpensesData}
            handleInputExpenses={handleInputExpenses}
            calculateMonthlyExpenses={calculateMonthlyExpenses}
            calculateMonthlyIndirectExpensesTotal={
              calculateMonthlyIndirectExpensesTotal
            }
            calculateTotalAnnualExpenses={calculateTotalAnnualExpenses}
            handleScrollTable={handleScrollTable}
            IndirectExpensesRef={IndirectExpensesRef}
            handleIndirectMonthlyExpenses={handleIndirectMonthlyExpenses}
            filterIndirectExpensesData={filterIndirectExpensesData}
            setFilterIndirectExpensesData={setFilterIndirectExpensesData}
            calculateIndirectTotalByProject={calculateIndirectTotalByProject}
            handleLaborIndirectTotalChange={handleLaborIndirectTotalChange}
          /> */}
        </>
      )}

      {loading && <Loading />}
      {downloading && <Loading />}
      {showDialogLabor && (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth="lg"
          aria-labelledby="confirmation-dialog"
          open={showDialogLabor}
        >
          <DialogContent className={classes.addDialog}>
            <div className={classes.popupHeader}>
              <span
                className={classes.closeIconAdd}
                onClick={() => {
                  setShowDialogLabor(false);
                  setIsEditDetails(false);
                  setLaborNotesHistory([]);
                }}
              >
                x
              </span>
              <div className={classes.addColumnTitle}>
                {" "}
                {isEditDetails ? `Notes` : `Add New`}
              </div>
            </div>
            <Grid container spacing={2}>
              {!isEditDetails && (
                <>
                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Select Project
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </div>
                    <Select
                      showSearch
                      mode="single"
                      style={{ width: "95%", zIndex: 9, marginBottom: "12px" }}
                      placeholder="Search to Select"
                      showArrow
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label.toLowerCase() ?? "").includes(
                          input.toLowerCase(),
                        )
                      }
                      options={projectOptions}
                      onChange={(val) =>
                        onChangeFormDataSelect(val, "projectId")
                      }
                      value={laborFormData.projectId}
                    />
                  </Grid>

                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>Select Employee</div>

                    <Select
                      showSearch
                      mode="single"
                      style={{ width: "95%", zIndex: 9, marginBottom: "12px" }}
                      placeholder="Search to Select"
                      showArrow
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label.toLowerCase() ?? "").includes(
                          input.toLowerCase(),
                        )
                      }
                      options={employeeOptions}
                      onChange={(val) => onChangeFormDataSelect(val, "emplId")}
                      value={laborFormData.emplId}
                    />
                  </Grid>

                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Financial Year <span style={{ color: "red" }}>*</span>
                    </div>

                    <Select
                      style={{ width: "95%", zIndex: 9, marginBottom: "12px" }}
                      placeholder="Select Financial Year"
                      options={financialYearOptions}
                      value={laborFormData.financialYear}
                      onChange={(val) =>
                        onChangeFormDataSelect(val, "financialYear")
                      }
                    />
                  </Grid>

                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Start Date <span style={{ color: "red" }}>*</span>
                    </div>

                    <CustomDatePicker
                      dateFormat={dateFormat}
                      dateValue={laborFormData.startDate}
                      setDateValue={(date) => {
                        const changedData = JSON.parse(
                          JSON.stringify(laborFormData),
                        );
                        changedData.startDate = formatDateBySetting(
                          getDateMoment(date),
                          "YYYY-MM-DD",
                        );
                        setLaborFormData(changedData);
                      }}
                      // Based on this dialog's own Financial Year field (laborFormData.financialYear),
                      // not the page-level Year dropdown — only that year and earlier years are
                      // pickable, future years stay disabled in the picker itself.
                      maxDate={
                        laborFormData.financialYear
                          ? `${laborFormData.financialYear}-12-31`
                          : null
                      }
                      itemWidth="95%"
                    />
                  </Grid>

                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Active Salary <span style={{ color: "red" }}>*</span>
                    </div>

                    <CustomTextField
                      selectedValue={laborFormData.activeSalary}
                      onChangeItem={(e) => onChangeFormData(e, "activeSalary")}
                      labelVal="Active Salary"
                      id="activeSalary"
                      type="number"
                      disabled={!!laborFormData.emplId}
                    />
                  </Grid>
                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Util <span style={{ color: "red" }}>*</span>
                    </div>

                    <CustomTextField
                      selectedValue={laborFormData.utilPct}
                      onChangeItem={(e) => onChangeFormData(e, "utilPct")}
                      labelVal="Util"
                      id="utilPct"
                      type="number"
                    />
                  </Grid>
                  {/* Only meaningful against the budget version — now that GET
                      .../indirect-dropdown flags it directly (`isBudget`), gate on that instead
                      of showing it unconditionally. */}
                  {isBudgetVersion && (
                    <Grid item lg={6}>
                      <div className={classes.fieldTitle}>Merit Increase %</div>

                      <CustomTextField
                        selectedValue={laborFormData.meritIncPct ?? ""}
                        onChangeItem={(e) => onChangeFormData(e, "meritIncPct")}
                        labelVal="Merit Increase %"
                        id="meritIncPct"
                        type="number"
                      />
                    </Grid>
                  )}
                  {/* Only meaningful for a new TBD row (no employee picked) on the budget
                      version — omitted from the request entirely for a real-employee row. */}
                  {isBudgetVersion && !laborFormData.emplId && (
                    <Grid item lg={6}>
                      <div className={classes.fieldTitle}>Job Title</div>

                      <CustomTextField
                        selectedValue={laborFormData.jobTitle}
                        onChangeItem={(e) => onChangeFormData(e, "jobTitle")}
                        labelVal="Job Title"
                        id="jobTitle"
                      />
                    </Grid>
                  )}
                </>
              )}
              <Grid style={{ marginTop: "12px" }} item lg={12}>
                {!isEditDetails && (
                  <div className={classes.fieldTitle}>Notes</div>
                )}

                <TextArea
                  rows={6}
                  placeholder={
                    (versionStatus.toLowerCase() === "open" &&
                      localStorage.getItem("accessType") === "RW" &&
                      departmentList.filter(
                        (val) => val.department === selectedDepartment,
                      )[0]?.isEditable) ||
                    isEditableBudget
                      ? "Notes"
                      : ""
                  }
                  className="indirectNotes"
                  maxLength={2000}
                  disabled={
                    !(
                      (versionStatus.toLowerCase() === "open" &&
                        localStorage.getItem("accessType") === "RW" &&
                        departmentList.filter(
                          (val) => val.department === selectedDepartment,
                        )[0]?.isEditable) ||
                      isEditableBudget
                    )
                  }
                  value={laborFormData.notes}
                  onChange={(e) => onChangeFormData(e, "notes")}
                />
              </Grid>
            </Grid>
            {((versionStatus.toLowerCase() === "open" &&
              localStorage.getItem("accessType") === "RW" &&
              departmentList.filter(
                (val) => val.department === selectedDepartment,
              )[0]?.isEditable) ||
              isEditableBudget) && (
              <div className={classes.popupBtnsAdd}>
                <Button
                  className={classes.popupBtn}
                  variant="contained"
                  color="primary"
                  onClick={() => onAddLabor()}
                  disabled={
                    (!isEditDetails &&
                      (laborFormData.projectId === "" ||
                        laborFormData.startDate === "" ||
                        laborFormData.financialYear === "" ||
                        laborFormData.activeSalary === 0 ||
                        laborFormData.utilPct === 0)) ||
                    (isEditDetails && laborFormData.notes === "")
                  }
                >
                  Add
                </Button>
                <Button
                  variant="outlined"
                  className={classes.cancelBtnPopup}
                  color="primary"
                  onClick={() => {
                    setShowDialogLabor(false);
                    setIsEditDetails(false);
                    setLaborNotesHistory([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {isEditDetails && (
              <div style={{ marginTop: "16px" }}>
                <table
                  className="firstTableFin"
                  cellPadding={0}
                  cellSpacing={0}
                  style={{ width: "100%", tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          height: "28px",
                          padding: "4px 8px",
                          width: "32px",
                        }}
                      ></th>
                      <th style={{ height: "28px", padding: "4px 8px" }}>
                        Notes History
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {laborNotesHistory.length > 0 ? (
                      laborNotesHistory.map((entry, i) => (
                        <tr key={i}>
                          <td
                            style={{
                              padding: "4px 8px",
                              overflow: "visible",
                              textOverflow: "clip",
                              whiteSpace: "normal",
                            }}
                          >
                            <DeleteForeverIcon
                              style={{
                                color: "red",
                                fontSize: "18px",
                                cursor: "pointer",
                              }}
                              titleAccess="Delete"
                              onClick={() => handleDeleteLaborNote(i)}
                            />
                          </td>
                          <td
                            style={{
                              padding: "4px 8px",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {entry.text}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            color: "#888",
                          }}
                        >
                          No Data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
      {showDialogNew && (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth="lg"
          aria-labelledby="confirmation-dialog"
          open={showDialogNew}
        >
          <DialogContent className={classes.addDialog}>
            <div className={classes.popupHeader}>
              <span
                className={classes.closeIconAdd}
                onClick={() => {
                  setShowDialogNew(false);
                  setIsEditDetails(false);
                  setNonLaborNotesHistory([]);
                }}
              >
                x
              </span>
              <div className={classes.addColumnTitle}>
                {" "}
                {isEditDetails ? `Notes` : `Add New`}
              </div>
            </div>
            <Grid container spacing={2}>
              {!isEditDetails && (
                <>
                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Select Project
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </div>

                    <Select
                      showSearch
                      mode="single"
                      style={{ width: "95%", zIndex: 9, marginBottom: "12px" }}
                      placeholder="Search to Select"
                      showArrow
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label.toLowerCase() ?? "").includes(
                          input.toLowerCase(),
                        )
                      }
                      options={projectOptions}
                      onChange={(val) =>
                        onChangeFormNonLaborSelect(val, "projectId")
                      }
                      value={nonLaborFormData.projectId}
                    />
                  </Grid>

                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Select Account
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </div>

                    <Select
                      showSearch
                      mode="single"
                      style={{ width: "95%", zIndex: 9, marginBottom: "12px" }}
                      placeholder="Search to Select"
                      showArrow
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label.toLowerCase() ?? "").includes(
                          input.toLowerCase(),
                        )
                      }
                      options={accountOptions}
                      onChange={(val) =>
                        onChangeFormNonLaborSelect(val, "accountId")
                      }
                      value={nonLaborFormData.accountId}
                    />
                  </Grid>

                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Total <span style={{ color: "red" }}>*</span>
                    </div>

                    <CustomTextField
                      selectedValue={nonLaborFormData.totalAmount}
                      onChangeItem={(e) =>
                        onChangeFormNonLaborData(e, "totalAmount")
                      }
                      labelVal="Total"
                      id="total"
                      type="number"
                    />
                  </Grid>
                  <Grid item lg={6}>
                    <div className={classes.fieldTitle}>
                      Financial Year <span style={{ color: "red" }}>*</span>
                    </div>

                    <Select
                      style={{ width: "95%", zIndex: 9, marginBottom: "12px" }}
                      placeholder="Select Financial Year"
                      options={financialYearOptions}
                      value={nonLaborFormData.financialYear}
                      onChange={(val) =>
                        onChangeFormNonLaborSelect(val, "financialYear")
                      }
                    />
                  </Grid>
                </>
              )}
              <Grid style={{ marginTop: "12px" }} item lg={12}>
                {!isEditDetails && (
                  <div className={classes.fieldTitle}>Notes</div>
                )}

                <TextArea
                  rows={6}
                  placeholder={
                    (versionStatus.toLowerCase() === "open" &&
                      localStorage.getItem("accessType") === "RW" &&
                      departmentList.filter(
                        (val) => val.department === selectedDepartment,
                      )[0]?.isEditable) ||
                    isEditableBudget
                      ? "Notes"
                      : ""
                  }
                  className="indirectNotes"
                  maxLength={2000}
                  value={nonLaborFormData.notes}
                  disabled={
                    !(
                      (versionStatus.toLowerCase() === "open" &&
                        localStorage.getItem("accessType") === "RW" &&
                        departmentList.filter(
                          (val) => val.department === selectedDepartment,
                        )[0]?.isEditable) ||
                      isEditableBudget
                    )
                  }
                  onChange={(e) => onChangeFormNonLaborData(e, "notes")}
                />
              </Grid>
            </Grid>
            {((versionStatus.toLowerCase() === "open" &&
              localStorage.getItem("accessType") === "RW" &&
              departmentList.filter(
                (val) => val.department === selectedDepartment,
              )[0]?.isEditable) ||
              isEditableBudget) && (
              <div className={classes.popupBtnsAdd}>
                <Button
                  className={classes.popupBtn}
                  variant="contained"
                  color="primary"
                  onClick={() => onAddNonLabor()}
                  disabled={
                    (!isEditDetails &&
                      (nonLaborFormData.projectId === "" ||
                        nonLaborFormData.accountId === "" ||
                        nonLaborFormData.financialYear === "" ||
                        nonLaborFormData.totalAmount === 0)) ||
                    (isEditDetails && nonLaborFormData.notes === "")
                  }
                >
                  Add
                </Button>
                <Button
                  variant="outlined"
                  className={classes.cancelBtnPopup}
                  color="primary"
                  onClick={() => {
                    setShowDialogNew(false);
                    setIsEditDetails(false);
                    setNonLaborNotesHistory([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {isEditDetails && (
              <div style={{ marginTop: "16px" }}>
                <table
                  className="firstTableFin"
                  cellPadding={0}
                  cellSpacing={0}
                  style={{ width: "100%", tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          height: "28px",
                          padding: "4px 8px",
                          width: "32px",
                        }}
                      ></th>
                      <th style={{ height: "28px", padding: "4px 8px" }}>
                        Notes History
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {nonLaborNotesHistory.length > 0 ? (
                      nonLaborNotesHistory.map((entry, i) => (
                        <tr key={i}>
                          <td
                            style={{
                              padding: "4px 8px",
                              overflow: "visible",
                              textOverflow: "clip",
                              whiteSpace: "normal",
                            }}
                          >
                            <DeleteForeverIcon
                              style={{
                                color: "red",
                                fontSize: "18px",
                                cursor: "pointer",
                              }}
                              titleAccess="Delete"
                              onClick={() => handleDeleteNonLaborNote(i)}
                            />
                          </td>
                          <td
                            style={{
                              padding: "4px 8px",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {entry.text}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            textAlign: "center",
                            padding: "8px",
                            color: "#888",
                          }}
                        >
                          No Data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
      <Drawer
        anchor="right"
        open={showUploadDrawer}
        onClose={() => setShowUploadDrawer(false)}
      >
        <div className={classes.uploadDrawerSection}>
          <div className={classes.uploadDrawerTitle}>
            {uploadMode === "data" ? "Upload Data" : "Upload Attachment"}
          </div>
          <div className={classes.uploadDrawerDescription}>
            {uploadMode === "data"
              ? "Please Drag and Drop or select the budget workbook to apply to the Non-Labor forecast"
              : "Please Drag and Drop or select the required files"}
          </div>
          <div
            className={classes.uploadDrawerClose}
            onClick={() => setShowUploadDrawer(false)}
          >
            <CloseIcon className={classes.uploadDrawerCloseIcon} />
          </div>
          {planFileError && (
            <div className={classes.uploadDrawerError}>{planFileError}</div>
          )}
          <FileUpload getFormData={handleUploadPlanFile} fieldName="file" />
        </div>
        {planFileUploading && <Loading />}
      </Drawer>

      {showPlanFilesDialog && (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth="md"
          aria-labelledby="plan-files-dialog"
          open={showPlanFilesDialog}
        >
          <DialogContent className={classes.addDialog}>
            <div className={classes.popupHeader}>
              <span
                className={classes.closeIconAdd}
                onClick={() => setShowPlanFilesDialog(false)}
              >
                x
              </span>
              <div className={classes.addColumnTitle}>Attachments</div>
            </div>

            {planFileError && (
              <div className={classes.uploadDrawerError}>{planFileError}</div>
            )}

            <table
              className="firstTableFin firstTableFinAttachemts"
              cellPadding={0}
              cellSpacing={0}
              style={{ width: "100%", tableLayout: "fixed" }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "4px 8px" }}>File Name</th>
                  <th style={{ padding: "4px 8px" }}>Uploaded By</th>
                  <th style={{ padding: "4px 8px" }}>Uploaded At</th>
                  <th style={{ padding: "4px 8px", width: "100px" }}></th>
                </tr>
              </thead>
              <tbody>
                {planFiles.length > 0 ? (
                  planFiles.map((file) => (
                    <tr key={file.id}>
                      <td
                        style={{ padding: "4px 8px", wordBreak: "break-word" }}
                      >
                        {file.fileName}
                      </td>
                      <td style={{ padding: "4px 8px" }}>{file.uploadedBy}</td>
                      <td style={{ padding: "4px 8px" }}>
                        {file.uploadedAt
                          ? new Date(file.uploadedAt).toLocaleString()
                          : ""}
                      </td>
                      <td
                        style={{
                          padding: "4px 8px",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{ cursor: "pointer" }}
                          title="Download"
                          onClick={() => handleDownloadPlanFile(file)}
                        >
                          <DownloadIcon
                            background="#0070D2"
                            color="#fff"
                            width="28"
                            height="28"
                          />
                        </span>
                        {isEditableBudget && (
                          <DeleteForeverIcon
                            style={{
                              color: "red",
                              fontSize: "20px",
                              cursor: "pointer",
                            }}
                            titleAccess="Delete"
                            onClick={() => onDeletePlanFileClick(file)}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "8px",
                        color: "#888",
                      }}
                    >
                      {planFilesLoading
                        ? "Loading..."
                        : "No files uploaded yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DialogContent>
          {planFilesLoading && <Loading />}
        </Dialog>
      )}
      {emptyErrMsg && (
        <ActionMessages type={msgType} isClosed={isClosed} message={msgText} />
      )}
      {confirmOpen && (
        <ConfirmationDialog
          cancelClick={cancelConfirmClick}
          okClick={okConfirmClick}
          confirmText="Do you want to delete this item?"
          closeDialog={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
};
export default IndirectSummaryTab;
